const CURRENT_PRICES = {
  BTC: 68250,
  ETH: 3350,
};

const ACCOUNT_KEY = 'options-demo-portfolio-main';

const isoAtUtcHour = (date, hour = 12) => {
  const next = new Date(date);
  next.setUTCHours(hour, 0, 0, 0);
  return next.toISOString();
};

const daysAgo = (days) => {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - days);
  return isoAtUtcHour(date);
};

const daysFromNow = (days) => {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + days);
  return isoAtUtcHour(date);
};

const toMs = (isoString) => new Date(isoString).getTime();

const toUsd = (value, currency) => {
  if (value === null || value === undefined) return null;
  return Number((Number(value) * CURRENT_PRICES[currency]).toFixed(8));
};

const expirationLabel = (isoString) => {
  const date = new Date(isoString);
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  }).format(date).replace(/\s/g, '').toUpperCase();
};

const DTE_BUCKETS = [
  { key: 'dte_0_7', label: '0–7', min: 0, max: 7 },
  { key: 'dte_8_14', label: '8–14', min: 8, max: 14 },
  { key: 'dte_15_30', label: '15–30', min: 15, max: 30 },
  { key: 'dte_31_45', label: '31–45', min: 31, max: 45 },
  { key: 'dte_46_60', label: '46–60', min: 46, max: 60 },
  { key: 'dte_61_90', label: '61–90', min: 61, max: 90 },
  { key: 'dte_90_plus', label: '90+', min: 90, max: null },
];

const DELTA_BUCKETS = [
  { key: 'delta_0_10', label: '0.00–0.10', min: 0, max: 0.1 },
  { key: 'delta_10_15', label: '0.10–0.15', min: 0.1, max: 0.15 },
  { key: 'delta_15_20', label: '0.15–0.20', min: 0.15, max: 0.2 },
  { key: 'delta_20_30', label: '0.20–0.30', min: 0.2, max: 0.3 },
  { key: 'delta_30_plus', label: '0.30+', min: 0.3, max: null },
];

const DURATION_BUCKETS = [
  { key: 'duration_lt_1', label: '<1d', min: 0, max: 1 },
  { key: 'duration_1_3', label: '1–3d', min: 1, max: 3 },
  { key: 'duration_4_7', label: '4–7d', min: 4, max: 7 },
  { key: 'duration_8_14', label: '8–14d', min: 8, max: 14 },
  { key: 'duration_15_30', label: '15–30d', min: 15, max: 30 },
  { key: 'duration_30_plus', label: '30+d', min: 30, max: null },
];

const deriveDemoEntryDelta = ({ strategyLabel, optionType, positionSide, currentDelta = null }) => {
  if (currentDelta !== null && currentDelta !== undefined) {
    return Number((Number(currentDelta) * 0.92).toFixed(4));
  }

  if (strategyLabel === 'SHORT_STRANGLE') {
    if (positionSide === 'SHORT' && optionType === 'PUT') return 0.16;
    if (positionSide === 'SHORT' && optionType === 'CALL') return -0.16;
  }

  if (strategyLabel === 'IRON_CONDOR') {
    if (positionSide === 'SHORT' && optionType === 'PUT') return 0.18;
    if (positionSide === 'SHORT' && optionType === 'CALL') return -0.18;
    if (positionSide === 'LONG' && optionType === 'PUT') return -0.07;
    if (positionSide === 'LONG' && optionType === 'CALL') return 0.07;
  }

  if (strategyLabel === 'LONG_CALL') return 0.32;
  if (strategyLabel === 'LONG_PUT') return -0.32;
  return null;
};

const makeLeg = ({
  id,
  underlying,
  strategyLabel,
  optionType,
  positionSide,
  strike,
  quantity = 1,
  averageEntryPrice,
  averageExitPrice = null,
  currentMarkPrice = null,
  realizedBase = 0,
  unrealizedBase = 0,
  feesBase = 0,
  entryDelta = null,
  currentDelta = null,
  currentTheta = null,
  currentVega = null,
  expiration,
  status,
}) => {
  const quantityClosed = status === 'CLOSED' ? quantity : 0;
  const entryNotionalBase = Number(((averageEntryPrice || 0) * quantity).toFixed(8));
  const exitNotionalBase = Number((((averageExitPrice || 0) * quantityClosed)).toFixed(8));
  const netBase = Number((realizedBase + unrealizedBase - feesBase).toFixed(8));
  return {
    id,
    account_key: ACCOUNT_KEY,
    underlying,
    option_type: optionType,
    position_side: positionSide,
    strike,
    expiration,
    status,
    quantity_opened: quantity,
    quantity_closed: quantityClosed,
    quantity_remaining: status === 'OPEN' ? quantity : 0,
    average_entry_price: averageEntryPrice,
    average_exit_price: averageExitPrice,
    entry_notional_base: entryNotionalBase,
    exit_notional_base: exitNotionalBase,
    current_mark_price: currentMarkPrice,
    realized_pnl_base: Number(realizedBase.toFixed(8)),
    realized_pnl_usd: toUsd(realizedBase, underlying),
    unrealized_pnl_base: Number(unrealizedBase.toFixed(8)),
    unrealized_pnl_usd: toUsd(unrealizedBase, underlying),
    net_pnl_base: netBase,
    net_pnl_usd: toUsd(netBase, underlying),
    total_fees_base: Number(feesBase.toFixed(8)),
    total_fees_usd: toUsd(feesBase, underlying),
    entry_delta: entryDelta ?? deriveDemoEntryDelta({ strategyLabel, optionType, positionSide, currentDelta }),
    current_delta: currentDelta,
    current_theta: currentTheta,
    current_vega: currentVega,
  };
};

const buildOperationFromLegs = ({
  id,
  underlying,
  strategyLabel,
  status,
  openedAgo,
  closedAgo = null,
  expirationInDays,
  legs,
  directionalBias = null,
  longTermDirectionalCandidate = false,
  note = null,
}) => {
  const openedAt = daysAgo(openedAgo);
  const closedAt = closedAgo === null ? null : daysAgo(closedAgo);
  const expiration = expirationInDays >= 0 ? daysFromNow(expirationInDays) : daysAgo(Math.abs(expirationInDays));

  const operationLegs = legs.map((leg, index) => makeLeg({
    id: `${id}-leg-${index + 1}`,
    underlying,
    strategyLabel,
    expiration,
    status,
    ...leg,
  }));

  const realizedBase = Number(operationLegs.reduce((sum, leg) => sum + Number(leg.realized_pnl_base || 0), 0).toFixed(8));
  const unrealizedBase = Number(operationLegs.reduce((sum, leg) => sum + Number(leg.unrealized_pnl_base || 0), 0).toFixed(8));
  const feesBase = Number(operationLegs.reduce((sum, leg) => sum + Number(leg.total_fees_base || 0), 0).toFixed(8));
  const netBase = Number((realizedBase + unrealizedBase - feesBase).toFixed(8));

  const summary = {
    id,
    account_key: ACCOUNT_KEY,
    underlying,
    strategy_label: strategyLabel,
    status,
    opened_at: openedAt,
    closed_at: closedAt,
    expiration,
    expiration_label: expirationLabel(expiration),
    realized_pnl_base: realizedBase,
    realized_pnl_usd: toUsd(realizedBase, underlying),
    unrealized_pnl_base: unrealizedBase,
    unrealized_pnl_usd: toUsd(unrealizedBase, underlying),
    net_pnl_base: netBase,
    net_pnl_usd: toUsd(netBase, underlying),
    total_fees_base: feesBase,
    total_fees_usd: toUsd(feesBase, underlying),
    directional_bias: directionalBias,
    long_term_directional_candidate: longTermDirectionalCandidate,
    dte_at_entry: Math.max(0, expirationInDays + openedAgo),
    note,
  };

  return {
    summary,
    detail: {
      operation: summary,
      legs: operationLegs,
    },
  };
};

const operationSeeds = [
  buildOperationFromLegs({
    id: 'demo-btc-ss-001',
    underlying: 'BTC',
    strategyLabel: 'SHORT_STRANGLE',
    status: 'CLOSED',
    openedAgo: 172,
    closedAgo: 161,
    expirationInDays: -150,
    legs: [
      { optionType: 'PUT', positionSide: 'SHORT', strike: 56000, averageEntryPrice: 0.043, averageExitPrice: 0.019, realizedBase: 0.025, feesBase: 0.002 },
      { optionType: 'CALL', positionSide: 'SHORT', strike: 76000, averageEntryPrice: 0.038, averageExitPrice: 0.015, realizedBase: 0.02, feesBase: 0.002 },
    ],
  }),
  buildOperationFromLegs({
    id: 'demo-btc-ss-002',
    underlying: 'BTC',
    strategyLabel: 'SHORT_STRANGLE',
    status: 'CLOSED',
    openedAgo: 160,
    closedAgo: 148,
    expirationInDays: -150,
    legs: [
      { optionType: 'PUT', positionSide: 'SHORT', strike: 57500, averageEntryPrice: 0.029, averageExitPrice: 0.041, realizedBase: -0.009, feesBase: 0.0015 },
      { optionType: 'CALL', positionSide: 'SHORT', strike: 79000, averageEntryPrice: 0.031, averageExitPrice: 0.038, realizedBase: -0.005, feesBase: 0.0025 },
    ],
  }),
  buildOperationFromLegs({
    id: 'demo-btc-long-put-001',
    underlying: 'BTC',
    strategyLabel: 'LONG_PUT',
    status: 'CLOSED',
    openedAgo: 138,
    closedAgo: 129,
    expirationInDays: -120,
    directionalBias: 'BEARISH',
    longTermDirectionalCandidate: true,
    legs: [
      { optionType: 'PUT', positionSide: 'LONG', strike: 61000, averageEntryPrice: 0.022, averageExitPrice: 0.046, realizedBase: 0.029, feesBase: 0.002 },
    ],
  }),
  buildOperationFromLegs({
    id: 'demo-btc-ss-003',
    underlying: 'BTC',
    strategyLabel: 'SHORT_STRANGLE',
    status: 'CLOSED',
    openedAgo: 92,
    closedAgo: 63,
    expirationInDays: -54,
    legs: [
      { optionType: 'PUT', positionSide: 'SHORT', strike: 58500, averageEntryPrice: 0.036, averageExitPrice: 0.051, realizedBase: -0.019, feesBase: 0.0025 },
      { optionType: 'CALL', positionSide: 'SHORT', strike: 81000, averageEntryPrice: 0.04, averageExitPrice: 0.054, realizedBase: -0.01, feesBase: 0.0025 },
    ],
  }),
  buildOperationFromLegs({
    id: 'demo-btc-ss-004',
    underlying: 'BTC',
    strategyLabel: 'SHORT_STRANGLE',
    status: 'OPEN',
    openedAgo: 58,
    expirationInDays: 34,
    legs: [
      { optionType: 'PUT', positionSide: 'SHORT', strike: 60000, averageEntryPrice: 0.031, currentMarkPrice: 0.017, realizedBase: 0.002, unrealizedBase: 0.016, feesBase: 0.002, currentDelta: 0.19, currentTheta: 0.0024, currentVega: -0.048 },
      { optionType: 'CALL', positionSide: 'SHORT', strike: 86000, averageEntryPrice: 0.028, currentMarkPrice: 0.02, realizedBase: 0.001, unrealizedBase: 0.009, feesBase: 0.002, currentDelta: -0.13, currentTheta: 0.0018, currentVega: -0.037 },
    ],
  }),
  buildOperationFromLegs({
    id: 'demo-btc-ic-001',
    underlying: 'BTC',
    strategyLabel: 'IRON_CONDOR',
    status: 'CLOSED',
    openedAgo: 48,
    closedAgo: 33,
    expirationInDays: -26,
    legs: [
      { optionType: 'PUT', positionSide: 'LONG', strike: 54000, averageEntryPrice: 0.011, averageExitPrice: 0.008, realizedBase: -0.002, feesBase: 0.0005 },
      { optionType: 'PUT', positionSide: 'SHORT', strike: 58000, averageEntryPrice: 0.024, averageExitPrice: 0.012, realizedBase: 0.013, feesBase: 0.0005 },
      { optionType: 'CALL', positionSide: 'SHORT', strike: 83000, averageEntryPrice: 0.021, averageExitPrice: 0.015, realizedBase: 0.009, feesBase: 0.0005 },
      { optionType: 'CALL', positionSide: 'LONG', strike: 87000, averageEntryPrice: 0.009, averageExitPrice: 0.006, realizedBase: -0.0005, feesBase: 0.0005 },
    ],
  }),
  buildOperationFromLegs({
    id: 'demo-btc-long-call-001',
    underlying: 'BTC',
    strategyLabel: 'LONG_CALL',
    status: 'OPEN',
    openedAgo: 16,
    expirationInDays: 28,
    directionalBias: 'BULLISH',
    longTermDirectionalCandidate: true,
    legs: [
      { optionType: 'CALL', positionSide: 'LONG', strike: 74000, averageEntryPrice: 0.018, currentMarkPrice: 0.033, realizedBase: 0.001, unrealizedBase: 0.014, feesBase: 0.0015, currentDelta: 0.41, currentTheta: -0.0021, currentVega: 0.112 },
    ],
  }),
  buildOperationFromLegs({
    id: 'demo-btc-long-call-002',
    underlying: 'BTC',
    strategyLabel: 'LONG_CALL',
    status: 'CLOSED',
    openedAgo: 22,
    closedAgo: 11,
    expirationInDays: -8,
    directionalBias: 'BULLISH',
    longTermDirectionalCandidate: true,
    legs: [
      { optionType: 'CALL', positionSide: 'LONG', strike: 70000, averageEntryPrice: 0.016, averageExitPrice: 0.025, realizedBase: 0.0072, feesBase: 0.0012 },
    ],
  }),
  buildOperationFromLegs({
    id: 'demo-eth-ic-001',
    underlying: 'ETH',
    strategyLabel: 'IRON_CONDOR',
    status: 'CLOSED',
    openedAgo: 168,
    closedAgo: 156,
    expirationInDays: -150,
    legs: [
      { optionType: 'PUT', positionSide: 'LONG', strike: 2400, averageEntryPrice: 0.012, averageExitPrice: 0.008, realizedBase: -0.008, feesBase: 0.002 },
      { optionType: 'PUT', positionSide: 'SHORT', strike: 2600, averageEntryPrice: 0.037, averageExitPrice: 0.018, realizedBase: 0.072, feesBase: 0.002 },
      { optionType: 'CALL', positionSide: 'SHORT', strike: 3600, averageEntryPrice: 0.032, averageExitPrice: 0.015, realizedBase: 0.062, feesBase: 0.002 },
      { optionType: 'CALL', positionSide: 'LONG', strike: 3800, averageEntryPrice: 0.009, averageExitPrice: 0.007, realizedBase: -0.004, feesBase: 0.002 },
    ],
  }),
  buildOperationFromLegs({
    id: 'demo-eth-ss-001',
    underlying: 'ETH',
    strategyLabel: 'SHORT_STRANGLE',
    status: 'CLOSED',
    openedAgo: 132,
    closedAgo: 119,
    expirationInDays: -112,
    legs: [
      { optionType: 'PUT', positionSide: 'SHORT', strike: 2500, averageEntryPrice: 0.031, averageExitPrice: 0.019, realizedBase: 0.051, feesBase: 0.0025 },
      { optionType: 'CALL', positionSide: 'SHORT', strike: 3650, averageEntryPrice: 0.029, averageExitPrice: 0.013, realizedBase: 0.034, feesBase: 0.0025 },
    ],
  }),
  buildOperationFromLegs({
    id: 'demo-eth-long-put-001',
    underlying: 'ETH',
    strategyLabel: 'LONG_PUT',
    status: 'CLOSED',
    openedAgo: 106,
    closedAgo: 97,
    expirationInDays: -88,
    directionalBias: 'BEARISH',
    longTermDirectionalCandidate: true,
    legs: [
      { optionType: 'PUT', positionSide: 'LONG', strike: 2750, averageEntryPrice: 0.024, averageExitPrice: 0.009, realizedBase: -0.0775, feesBase: 0.0025 },
    ],
  }),
  buildOperationFromLegs({
    id: 'demo-eth-long-call-001',
    underlying: 'ETH',
    strategyLabel: 'LONG_CALL',
    status: 'CLOSED',
    openedAgo: 74,
    closedAgo: 66,
    expirationInDays: -58,
    directionalBias: 'BULLISH',
    longTermDirectionalCandidate: true,
    legs: [
      { optionType: 'CALL', positionSide: 'LONG', strike: 3200, averageEntryPrice: 0.017, averageExitPrice: 0.047, realizedBase: 0.093, feesBase: 0.003 },
    ],
  }),
  buildOperationFromLegs({
    id: 'demo-eth-ic-002',
    underlying: 'ETH',
    strategyLabel: 'IRON_CONDOR',
    status: 'OPEN',
    openedAgo: 44,
    expirationInDays: 20,
    legs: [
      { optionType: 'PUT', positionSide: 'LONG', strike: 2500, averageEntryPrice: 0.01, currentMarkPrice: 0.009, unrealizedBase: -0.002, feesBase: 0.001, currentDelta: -0.07, currentTheta: -0.001, currentVega: 0.08 },
      { optionType: 'PUT', positionSide: 'SHORT', strike: 2800, averageEntryPrice: 0.021, currentMarkPrice: 0.022, unrealizedBase: -0.004, feesBase: 0.001, currentDelta: 0.18, currentTheta: 0.0026, currentVega: -0.16 },
      { optionType: 'CALL', positionSide: 'SHORT', strike: 3650, averageEntryPrice: 0.022, currentMarkPrice: 0.019, unrealizedBase: 0.003, feesBase: 0.001, currentDelta: -0.16, currentTheta: 0.0023, currentVega: -0.15 },
      { optionType: 'CALL', positionSide: 'LONG', strike: 3900, averageEntryPrice: 0.008, currentMarkPrice: 0.009, unrealizedBase: -0.003, feesBase: 0.001, currentDelta: 0.09, currentTheta: -0.0012, currentVega: 0.07 },
    ],
  }),
  buildOperationFromLegs({
    id: 'demo-eth-ss-002',
    underlying: 'ETH',
    strategyLabel: 'SHORT_STRANGLE',
    status: 'CLOSED',
    openedAgo: 58,
    closedAgo: 50,
    expirationInDays: -45,
    legs: [
      { optionType: 'PUT', positionSide: 'SHORT', strike: 2700, averageEntryPrice: 0.022, averageExitPrice: 0.033, realizedBase: -0.023, feesBase: 0.002 },
      { optionType: 'CALL', positionSide: 'SHORT', strike: 3800, averageEntryPrice: 0.02, averageExitPrice: 0.028, realizedBase: -0.021, feesBase: 0.002 },
    ],
  }),
  buildOperationFromLegs({
    id: 'demo-eth-long-call-002',
    underlying: 'ETH',
    strategyLabel: 'LONG_CALL',
    status: 'CLOSED',
    openedAgo: 28,
    closedAgo: 18,
    expirationInDays: -12,
    directionalBias: 'BULLISH',
    longTermDirectionalCandidate: true,
    legs: [
      { optionType: 'CALL', positionSide: 'LONG', strike: 3300, averageEntryPrice: 0.015, averageExitPrice: 0.052, realizedBase: 0.2535, feesBase: 0.0035 },
    ],
  }),
];

const operationList = operationSeeds.map((item) => item.summary);
const operationDetailsMap = Object.fromEntries(operationSeeds.map((item) => [item.summary.id, item.detail]));

const buildRollLink = ({ id, campaignId, sourceOperationId, targetOperationId, rollTypes, createdAgo, linkPairs }) => ({
  id,
  campaign_id: campaignId,
  account_key: ACCOUNT_KEY,
  source_operation_id: sourceOperationId,
  source_leg_ids: linkPairs.map((pair) => pair.sourceLegId),
  target_operation_id: targetOperationId,
  target_leg_ids: linkPairs.map((pair) => pair.targetLegId),
  roll_types: rollTypes,
  link_confidence: 'HIGH',
  origin: 'demo_portfolio',
  is_manual: false,
  context_note: 'DEMONSTRATIVE DATASET',
  evidence: [],
  created_at: daysAgo(createdAgo),
  updated_at: daysAgo(createdAgo),
});

const campaignSeeds = [
  {
    id: 'demo-campaign-btc-same-exp',
    operationIds: ['demo-btc-ss-001', 'demo-btc-ss-002'],
    rollLinks: [
      buildRollLink({
        id: 'demo-roll-btc-same-exp',
        campaignId: 'demo-campaign-btc-same-exp',
        sourceOperationId: 'demo-btc-ss-001',
        targetOperationId: 'demo-btc-ss-002',
        rollTypes: ['SAME_EXPIRATION'],
        createdAgo: 160,
        linkPairs: [
          { sourceLegId: 'demo-btc-ss-001-leg-1', targetLegId: 'demo-btc-ss-002-leg-1' },
          { sourceLegId: 'demo-btc-ss-001-leg-2', targetLegId: 'demo-btc-ss-002-leg-2' },
        ],
      }),
    ],
  },
  {
    id: 'demo-campaign-btc-exp-roll',
    operationIds: ['demo-btc-ss-003', 'demo-btc-ss-004'],
    rollLinks: [
      buildRollLink({
        id: 'demo-roll-btc-exp',
        campaignId: 'demo-campaign-btc-exp-roll',
        sourceOperationId: 'demo-btc-ss-003',
        targetOperationId: 'demo-btc-ss-004',
        rollTypes: ['EXPIRATION_ROLL'],
        createdAgo: 58,
        linkPairs: [
          { sourceLegId: 'demo-btc-ss-003-leg-1', targetLegId: 'demo-btc-ss-004-leg-1' },
          { sourceLegId: 'demo-btc-ss-003-leg-2', targetLegId: 'demo-btc-ss-004-leg-2' },
        ],
      }),
    ],
  },
  {
    id: 'demo-campaign-eth-range',
    operationIds: ['demo-eth-ic-001', 'demo-eth-ic-002'],
    rollLinks: [],
  },
  {
    id: 'demo-campaign-eth-directional',
    operationIds: ['demo-eth-long-put-001', 'demo-eth-long-call-001', 'demo-eth-long-call-002'],
    rollLinks: [],
  },
];

const buildCampaignSummary = (campaignSeed) => {
  const operations = campaignSeed.operationIds.map((id) => operationList.find((item) => item.id === id)).filter(Boolean);
  const legs = campaignSeed.operationIds.flatMap((id) => operationDetailsMap[id]?.legs || []);
  const underlying = operations[0]?.underlying || 'BTC';
  const status = operations.some((item) => item.status === 'OPEN') ? 'OPEN' : 'CLOSED';
  const campaignNetBase = Number(operations.reduce((sum, item) => sum + Number(item.net_pnl_base || 0), 0).toFixed(8));
  const openUnrealizedBase = Number(operations.reduce((sum, item) => sum + Number(item.unrealized_pnl_base || 0), 0).toFixed(8));
  const totalFeesBase = Number(operations.reduce((sum, item) => sum + Number(item.total_fees_base || 0), 0).toFixed(8));
  const grossRealizedBase = Number(operations.reduce((sum, item) => sum + Number(item.realized_pnl_base || 0), 0).toFixed(8));
  const detail = {
    campaign: {
      id: campaignSeed.id,
      account_key: ACCOUNT_KEY,
      underlying,
      strategy_origin: operations[0]?.strategy_label || 'SHORT_STRANGLE',
      original_operation_id: operations[0]?.id,
      operation_ids: operations.map((item) => item.id),
      leg_ids: legs.map((item) => item.id),
      roll_link_ids: campaignSeed.rollLinks.map((item) => item.id),
      opened_at: operations[0]?.opened_at,
      status,
      gross_realized_pnl_base: grossRealizedBase,
      realized_fees_base: totalFeesBase,
      net_realized_pnl_base: Number((grossRealizedBase - totalFeesBase).toFixed(8)),
      open_unrealized_pnl_base: openUnrealizedBase,
      total_fees_base: totalFeesBase,
      campaign_net_pnl_base: campaignNetBase,
      gross_realized_pnl_usd: toUsd(grossRealizedBase, underlying),
      realized_fees_usd: toUsd(totalFeesBase, underlying),
      net_realized_pnl_usd: toUsd(grossRealizedBase - totalFeesBase, underlying),
      open_unrealized_pnl_usd: toUsd(openUnrealizedBase, underlying),
      total_fees_usd: toUsd(totalFeesBase, underlying),
      campaign_net_pnl_usd: toUsd(campaignNetBase, underlying),
      operation_count: operations.length,
      leg_count: legs.length,
      roll_link_count: campaignSeed.rollLinks.length,
      build_source: 'demo_portfolio',
      build_version: 1,
      updated_at: operations[operations.length - 1]?.closed_at || operations[operations.length - 1]?.opened_at,
    },
    operations,
    legs,
    roll_links: campaignSeed.rollLinks,
  };

  return detail;
};

const campaignDetailsMap = Object.fromEntries(campaignSeeds.map((seed) => {
  const detail = buildCampaignSummary(seed);
  return [seed.id, detail];
}));

const campaignSummaries = Object.values(campaignDetailsMap).map((detail) => detail.campaign);

const cashFlowEvents = [
  {
    id: 'demo-cashflow-btc-deposit',
    currency: 'BTC',
    timestamp: daysAgo(130),
    timestamp_ms: toMs(daysAgo(130)),
    type: 'deposit',
    amount_native: 1.2,
    amount_usd: toUsd(1.2, 'BTC'),
    transaction_reference: 'DEMO-BTC-DEP-001',
  },
  {
    id: 'demo-cashflow-btc-withdrawal',
    currency: 'BTC',
    timestamp: daysAgo(60),
    timestamp_ms: toMs(daysAgo(60)),
    type: 'withdrawal',
    amount_native: -0.18,
    amount_usd: toUsd(-0.18, 'BTC'),
    transaction_reference: 'DEMO-BTC-WITH-001',
  },
  {
    id: 'demo-cashflow-eth-deposit',
    currency: 'ETH',
    timestamp: daysAgo(124),
    timestamp_ms: toMs(daysAgo(124)),
    type: 'deposit',
    amount_native: 8,
    amount_usd: toUsd(8, 'ETH'),
    transaction_reference: 'DEMO-ETH-DEP-001',
  },
];

const btcPeriods = [
  {
    id: 'demo-capital-btc-1',
    account_key: ACCOUNT_KEY,
    currency: 'BTC',
    period_index: 1,
    status: 'CLOSED',
    started_at: daysAgo(180),
    started_timestamp_ms: toMs(daysAgo(180)),
    ended_at: daysAgo(130),
    ended_timestamp_ms: toMs(daysAgo(130)),
    starting_equity_native: 1.05,
    starting_equity_usd: toUsd(1.05, 'BTC'),
    ending_equity_native: 1.22,
    ending_equity_usd: toUsd(1.22, 'BTC'),
    period_equity_change_native: 0.17,
    period_equity_change_usd: toUsd(0.17, 'BTC'),
    return_pct: 0.1619047619,
    return_pct_reason: null,
    start_anchor_type: 'opening_snapshot',
    start_anchor_reference_id: 'demo-btc-period-start-1',
    end_anchor_type: 'pre_cash_flow_snapshot',
    started_after_cash_flow_id: null,
    started_after_cash_flow_type: null,
    started_after_cash_flow_amount_native: null,
    started_after_cash_flow_amount_usd: null,
    closing_cash_flow_id: 'demo-cashflow-btc-deposit',
    closing_cash_flow_type: 'deposit',
    closing_cash_flow_amount_native: 1.2,
    closing_cash_flow_amount_usd: toUsd(1.2, 'BTC'),
    closing_cash_flow_timestamp: daysAgo(130),
    closing_equity_before_cash_flow_native: 1.22,
    closing_equity_after_cash_flow_native: 2.42,
    deposits_since_start_native: 0,
    withdrawals_since_start_native: 0,
    transfers_since_start_native: 0,
  },
  {
    id: 'demo-capital-btc-2',
    account_key: ACCOUNT_KEY,
    currency: 'BTC',
    period_index: 2,
    status: 'CLOSED',
    started_at: daysAgo(130),
    started_timestamp_ms: toMs(daysAgo(130)),
    ended_at: daysAgo(60),
    ended_timestamp_ms: toMs(daysAgo(60)),
    starting_equity_native: 2.42,
    starting_equity_usd: toUsd(2.42, 'BTC'),
    ending_equity_native: 2.26,
    ending_equity_usd: toUsd(2.26, 'BTC'),
    period_equity_change_native: -0.16,
    period_equity_change_usd: toUsd(-0.16, 'BTC'),
    return_pct: -0.0661157025,
    return_pct_reason: null,
    start_anchor_type: 'post_cash_flow',
    start_anchor_reference_id: 'demo-cashflow-btc-deposit',
    end_anchor_type: 'pre_cash_flow_snapshot',
    started_after_cash_flow_id: 'demo-cashflow-btc-deposit',
    started_after_cash_flow_type: 'deposit',
    started_after_cash_flow_amount_native: 1.2,
    started_after_cash_flow_amount_usd: toUsd(1.2, 'BTC'),
    closing_cash_flow_id: 'demo-cashflow-btc-withdrawal',
    closing_cash_flow_type: 'withdrawal',
    closing_cash_flow_amount_native: -0.18,
    closing_cash_flow_amount_usd: toUsd(-0.18, 'BTC'),
    closing_cash_flow_timestamp: daysAgo(60),
    closing_equity_before_cash_flow_native: 2.26,
    closing_equity_after_cash_flow_native: 2.08,
    deposits_since_start_native: 1.2,
    withdrawals_since_start_native: 0,
    transfers_since_start_native: 0,
  },
  {
    id: 'demo-capital-btc-3',
    account_key: ACCOUNT_KEY,
    currency: 'BTC',
    period_index: 3,
    status: 'OPEN',
    started_at: daysAgo(60),
    started_timestamp_ms: toMs(daysAgo(60)),
    ended_at: null,
    ended_timestamp_ms: null,
    starting_equity_native: 2.08,
    starting_equity_usd: toUsd(2.08, 'BTC'),
    ending_equity_native: 2.1485,
    ending_equity_usd: toUsd(2.1485, 'BTC'),
    period_equity_change_native: 0.0685,
    period_equity_change_usd: toUsd(0.0685, 'BTC'),
    return_pct: 0.0329326923,
    return_pct_reason: null,
    start_anchor_type: 'post_cash_flow',
    start_anchor_reference_id: 'demo-cashflow-btc-withdrawal',
    end_anchor_type: 'current_snapshot',
    started_after_cash_flow_id: 'demo-cashflow-btc-withdrawal',
    started_after_cash_flow_type: 'withdrawal',
    started_after_cash_flow_amount_native: -0.18,
    started_after_cash_flow_amount_usd: toUsd(-0.18, 'BTC'),
    closing_cash_flow_id: null,
    closing_cash_flow_type: null,
    closing_cash_flow_amount_native: null,
    closing_cash_flow_amount_usd: null,
    closing_cash_flow_timestamp: null,
    closing_equity_before_cash_flow_native: null,
    closing_equity_after_cash_flow_native: null,
    deposits_since_start_native: 0,
    withdrawals_since_start_native: -0.18,
    transfers_since_start_native: 0,
    current_equity_native: 2.1485,
    current_equity_usd: toUsd(2.1485, 'BTC'),
    current_return_pct: 0.0329326923,
    current_return_pct_reason: null,
  },
];

const ethPeriods = [
  {
    id: 'demo-capital-eth-1',
    account_key: ACCOUNT_KEY,
    currency: 'ETH',
    period_index: 1,
    status: 'CLOSED',
    started_at: daysAgo(170),
    started_timestamp_ms: toMs(daysAgo(170)),
    ended_at: daysAgo(124),
    ended_timestamp_ms: toMs(daysAgo(124)),
    starting_equity_native: 12,
    starting_equity_usd: toUsd(12, 'ETH'),
    ending_equity_native: 12.65,
    ending_equity_usd: toUsd(12.65, 'ETH'),
    period_equity_change_native: 0.65,
    period_equity_change_usd: toUsd(0.65, 'ETH'),
    return_pct: 0.0541666667,
    return_pct_reason: null,
    start_anchor_type: 'opening_snapshot',
    start_anchor_reference_id: 'demo-eth-period-start-1',
    end_anchor_type: 'pre_cash_flow_snapshot',
    started_after_cash_flow_id: null,
    started_after_cash_flow_type: null,
    started_after_cash_flow_amount_native: null,
    started_after_cash_flow_amount_usd: null,
    closing_cash_flow_id: 'demo-cashflow-eth-deposit',
    closing_cash_flow_type: 'deposit',
    closing_cash_flow_amount_native: 8,
    closing_cash_flow_amount_usd: toUsd(8, 'ETH'),
    closing_cash_flow_timestamp: daysAgo(124),
    closing_equity_before_cash_flow_native: 12.65,
    closing_equity_after_cash_flow_native: 20.65,
    deposits_since_start_native: 0,
    withdrawals_since_start_native: 0,
    transfers_since_start_native: 0,
  },
  {
    id: 'demo-capital-eth-2',
    account_key: ACCOUNT_KEY,
    currency: 'ETH',
    period_index: 2,
    status: 'OPEN',
    started_at: daysAgo(124),
    started_timestamp_ms: toMs(daysAgo(124)),
    ended_at: null,
    ended_timestamp_ms: null,
    starting_equity_native: 20.65,
    starting_equity_usd: toUsd(20.65, 'ETH'),
    ending_equity_native: 20.47,
    ending_equity_usd: toUsd(20.47, 'ETH'),
    period_equity_change_native: -0.18,
    period_equity_change_usd: toUsd(-0.18, 'ETH'),
    return_pct: -0.008716707,
    return_pct_reason: null,
    start_anchor_type: 'post_cash_flow',
    start_anchor_reference_id: 'demo-cashflow-eth-deposit',
    end_anchor_type: 'current_snapshot',
    started_after_cash_flow_id: 'demo-cashflow-eth-deposit',
    started_after_cash_flow_type: 'deposit',
    started_after_cash_flow_amount_native: 8,
    started_after_cash_flow_amount_usd: toUsd(8, 'ETH'),
    closing_cash_flow_id: null,
    closing_cash_flow_type: null,
    closing_cash_flow_amount_native: null,
    closing_cash_flow_amount_usd: null,
    closing_cash_flow_timestamp: null,
    closing_equity_before_cash_flow_native: null,
    closing_equity_after_cash_flow_native: null,
    deposits_since_start_native: 8,
    withdrawals_since_start_native: 0,
    transfers_since_start_native: 0,
    current_equity_native: 20.47,
    current_equity_usd: toUsd(20.47, 'ETH'),
    current_return_pct: -0.008716707,
    current_return_pct_reason: null,
  },
];

const snapshotOffsets = [180, 160, 145, 130, 110, 90, 60, 45, 30, 15, 7, 0];
const btcSnapshotValues = [1.05, 1.1, 1.17, 1.22, 2.42, 2.38, 2.26, 2.08, 2.11, 2.14, 2.145, 2.1485];
const ethSnapshotValues = [12, 12.18, 12.42, 12.65, 20.65, 20.58, 20.52, 20.44, 20.38, 20.41, 20.45, 20.47];

const buildEquityHistory = () => {
  return snapshotOffsets.flatMap((offset, index) => {
    const snapshotAt = daysAgo(offset);
    const entries = [
      {
        id: `demo-equity-btc-${index + 1}`,
        account_key: ACCOUNT_KEY,
        snapshot_at: snapshotAt,
        snapshot_timestamp_ms: toMs(snapshotAt),
        currency: 'BTC',
        usd_index_name: 'btc_usd',
        usd_price: CURRENT_PRICES.BTC,
        usd_price_source: 'GENI Options demo dataset',
        usd_price_source_field: 'demo_mark',
        equity_native: btcSnapshotValues[index],
        equity_usd: toUsd(btcSnapshotValues[index], 'BTC'),
        realized_pnl_native: null,
        unrealized_pnl_native: null,
        total_pl_native: null,
      },
      {
        id: `demo-equity-eth-${index + 1}`,
        account_key: ACCOUNT_KEY,
        snapshot_at: snapshotAt,
        snapshot_timestamp_ms: toMs(snapshotAt),
        currency: 'ETH',
        usd_index_name: 'eth_usd',
        usd_price: CURRENT_PRICES.ETH,
        usd_price_source: 'GENI Options demo dataset',
        usd_price_source_field: 'demo_mark',
        equity_native: ethSnapshotValues[index],
        equity_usd: toUsd(ethSnapshotValues[index], 'ETH'),
        realized_pnl_native: null,
        unrealized_pnl_native: null,
        total_pl_native: null,
      },
    ];
    return entries;
  });
};

const equityHistory = buildEquityHistory();

const buildAccountSummary = (currency, equityNative) => ({
  currency,
  equity: equityNative,
  balance: Number((equityNative * 0.96).toFixed(8)),
  available_funds: Number((equityNative * 0.74).toFixed(8)),
  available_withdrawal_funds: Number((equityNative * 0.68).toFixed(8)),
  margin_balance: Number((equityNative * 0.82).toFixed(8)),
  initial_margin: Number((equityNative * 0.14).toFixed(8)),
  maintenance_margin: Number((equityNative * 0.1).toFixed(8)),
  close_out_margin: Number((equityNative * 0.18).toFixed(8)),
  projected_initial_margin: Number((equityNative * 0.17).toFixed(8)),
  projected_maintenance_margin: Number((equityNative * 0.12).toFixed(8)),
  projected_close_out_margin: Number((equityNative * 0.2).toFixed(8)),
  options_value: Number((equityNative * 0.22).toFixed(8)),
  options_pl: Number((equityNative * 0.01).toFixed(8)),
  options_session_upl: Number((equityNative * 0.004).toFixed(8)),
  options_session_rpl: Number((equityNative * -0.002).toFixed(8)),
  futures_pl: 0,
  futures_session_upl: 0,
  futures_session_rpl: 0,
  session_upl: Number((equityNative * 0.004).toFixed(8)),
  session_rpl: Number((equityNative * -0.002).toFixed(8)),
  total_pl: Number((equityNative * 0.002).toFixed(8)),
  fee_balance: Number((equityNative * 0.008).toFixed(8)),
  locked_balance: Number((equityNative * 0.05).toFixed(8)),
  spot_reserve: 0,
  additional_reserve: 0,
  delta_total: currency === 'BTC' ? 0.47 : 0.04,
  options_delta: currency === 'BTC' ? 0.47 : 0.04,
  options_gamma: 0,
  options_vega: currency === 'BTC' ? 0.027 : -0.16,
  options_theta: currency === 'BTC' ? 0.0021 : 0.0027,
});

const currentAccountSummaries = [
  buildAccountSummary('BTC', 2.1485),
  buildAccountSummary('ETH', 20.47),
];

const portfolioRiskDemo = {
  position_exposure_validated: true,
  wallet_note: 'DEMONSTRATIVO: o wallet inclui uma reserva em USDC para deixar visível a diferença entre composição da conta e risco direcional das opções.',
  note: 'DEMONSTRATIVO: Net Delta, Theta, Vega, Position Exposure % e as gregas por leg foram mockados de forma coerente apenas para ilustrar a separação entre Wallet Exposure e Position Exposure.',
  wallet_assets: [
    { currency: 'BTC', native_value: 2.1485, value_usd: toUsd(2.1485, 'BTC') },
    { currency: 'ETH', native_value: 20.47, value_usd: toUsd(20.47, 'ETH') },
    { currency: 'USDC', native_value: 32500, value_usd: 32500 },
  ],
};

const rangeBounds = ({ range = '30d', start = null, end = null }) => {
  const rangeKey = (range || '30d').toLowerCase();
  const endDate = end ? new Date(end) : new Date();
  if (rangeKey === 'all') {
    return { startAt: null, endAt: endDate };
  }
  if (rangeKey === 'custom') {
    return {
      startAt: start ? new Date(start) : null,
      endAt: end ? new Date(end) : endDate,
    };
  }
  const startDate = new Date(endDate);
  if (rangeKey === '30d') startDate.setUTCDate(startDate.getUTCDate() - 30);
  if (rangeKey === '90d') startDate.setUTCDate(startDate.getUTCDate() - 90);
  if (rangeKey === 'ytd') {
    startDate.setUTCMonth(0, 1);
    startDate.setUTCHours(0, 0, 0, 0);
  }
  return { startAt: startDate, endAt: endDate };
};

const operationOverlapsRange = (operation, bounds) => {
  if (!bounds.startAt) return true;
  const openedAt = new Date(operation.opened_at);
  const closedAt = operation.closed_at ? new Date(operation.closed_at) : null;
  return openedAt <= bounds.endAt && (!closedAt || closedAt >= bounds.startAt);
};

const filterByRange = (items, getStart, getEnd, bounds) => {
  if (!bounds.startAt) return [...items];
  return items.filter((item) => {
    const startAt = new Date(getStart(item));
    const endAt = getEnd(item) ? new Date(getEnd(item)) : startAt;
    return startAt <= bounds.endAt && endAt >= bounds.startAt;
  });
};

const buildRangeMeta = ({ range = '30d', start = null, end = null }) => {
  const bounds = rangeBounds({ range, start, end });
  const startAt = bounds.startAt || new Date(Math.min(...equityHistory.map((item) => item.snapshot_timestamp_ms)));
  const endAt = bounds.endAt;
  return {
    requested_range: range,
    start_at: start ? new Date(start).toISOString() : startAt.toISOString(),
    end_at: end ? new Date(end).toISOString() : endAt.toISOString(),
    start_timestamp_ms: startAt.getTime(),
    end_timestamp_ms: endAt.getTime(),
    is_current_end: !end,
  };
};

const analyticsClosedOperationInRange = (operation, bounds) => {
  if (operation.status !== 'CLOSED' || !operation.closed_at) return false;
  const closedAt = new Date(operation.closed_at);
  return (!bounds.startAt || closedAt >= bounds.startAt) && closedAt <= bounds.endAt;
};

const analyticsMatchesFilters = (item, underlying = 'all', strategy = 'all', strategyField = 'strategy_label') => {
  const underlyingOk = underlying === 'all' || String(item.underlying || '').toLowerCase() === String(underlying).toLowerCase();
  const strategyOk = strategy === 'all' || item[strategyField] === strategy;
  return underlyingOk && strategyOk;
};

const sumPresent = (values, digits = 8) => {
  const filtered = values.filter((value) => value !== null && value !== undefined).map(Number);
  if (!filtered.length) return null;
  return Number(filtered.reduce((sum, value) => sum + value, 0).toFixed(digits));
};

const meanValues = (values, digits = 8) => {
  const filtered = values.filter((value) => value !== null && value !== undefined).map(Number);
  if (!filtered.length) return null;
  return Number((filtered.reduce((sum, value) => sum + value, 0) / filtered.length).toFixed(digits));
};

const classifyOutcome = (value) => {
  if (value === null || value === undefined) return null;
  if (Number(value) > 0) return 'WIN';
  if (Number(value) < 0) return 'LOSS';
  return 'EVEN';
};

const buildValueStats = (items, pnlField, feesField) => {
  const positiveValues = items.filter((item) => item[pnlField] !== null && item[pnlField] !== undefined && Number(item[pnlField]) > 0).map((item) => Number(item[pnlField]));
  const negativeValues = items.filter((item) => item[pnlField] !== null && item[pnlField] !== undefined && Number(item[pnlField]) < 0).map((item) => Number(item[pnlField]));
  const feesValues = items.filter((item) => item[feesField] !== null && item[feesField] !== undefined).map((item) => Number(item[feesField]));

  const tradingTotal = Number(items.reduce((sum, item) => sum + Number(item[pnlField] || 0), 0).toFixed(12));
  const feesTotal = feesValues.length ? Number(feesValues.reduce((sum, value) => sum + value, 0).toFixed(12)) : null;
  const grossProfit = positiveValues.length ? Number(positiveValues.reduce((sum, value) => sum + value, 0).toFixed(12)) : null;
  const grossLossAbs = negativeValues.length ? Number(Math.abs(negativeValues.reduce((sum, value) => sum + value, 0)).toFixed(12)) : null;
  const averageWinner = meanValues(positiveValues, 12);
  const averageLoser = meanValues(negativeValues, 12);
  const grossTradingResult = feesTotal !== null ? Number((tradingTotal + feesTotal).toFixed(12)) : null;
  const winCount = positiveValues.length;
  const lossCount = negativeValues.length;
  const denominator = winCount + lossCount;
  const winRate = denominator > 0 ? Number((winCount / denominator).toFixed(8)) : null;
  const lossRate = denominator > 0 ? Number((lossCount / denominator).toFixed(8)) : null;
  const profitFactor = grossProfit !== null && grossLossAbs ? Number((grossProfit / grossLossAbs).toFixed(8)) : null;
  const payoffRatio = averageWinner !== null && averageLoser ? Number((averageWinner / Math.abs(averageLoser)).toFixed(8)) : null;
  const expectancy = winRate !== null && lossRate !== null && averageWinner !== null && averageLoser !== null
    ? Number(((winRate * averageWinner) - (lossRate * Math.abs(averageLoser))).toFixed(12))
    : null;
  const feeImpactPct = feesTotal !== null && grossTradingResult ? Number((feesTotal / Math.abs(grossTradingResult)).toFixed(8)) : null;

  return {
    win_count: winCount,
    loss_count: lossCount,
    gross_profit: grossProfit,
    gross_loss_abs: grossLossAbs,
    average_winner: averageWinner,
    average_loser: averageLoser,
    gross_trading_result: grossTradingResult,
    profit_factor: profitFactor,
    payoff_ratio: payoffRatio,
    expectancy,
    fee_impact_pct: feeImpactPct,
  };
};

const buildPremiumStats = (items) => {
  const eligible = items.filter((item) => item.premium_received_base !== null && item.premium_received_base !== undefined && item.premium_retained_base !== null && item.premium_retained_base !== undefined);
  const totalReceived = eligible.length ? Number(eligible.reduce((sum, item) => sum + Number(item.premium_received_base || 0), 0).toFixed(12)) : null;
  const totalRetained = eligible.length ? Number(eligible.reduce((sum, item) => sum + Number(item.premium_retained_base || 0), 0).toFixed(12)) : null;
  return {
    premium_captured_pct: totalReceived ? Number((totalRetained / totalReceived).toFixed(8)) : null,
    premium_captured_sample_count: eligible.length,
  };
};

const buildDteStats = (items) => ({
  average_entry_dte: meanValues(items.map((item) => item.dte_at_entry), 4),
  average_exit_dte: meanValues(items.map((item) => item.exit_dte), 4),
  average_dte_reduction: meanValues(items.map((item) => item.dte_reduction), 4),
});

const buildAnalyticsMetrics = (operations) => {
  const grouped = operations.reduce((acc, operation) => {
    const currency = String(operation.underlying || 'UNKNOWN').toUpperCase();
    acc[currency] = acc[currency] || [];
    acc[currency].push(operation);
    return acc;
  }, {});

  const usdStats = buildValueStats(operations, 'net_pnl_usd', 'total_fees_usd');
  const premiumStats = buildPremiumStats(operations);
  const dteStats = buildDteStats(operations);
  const evens = operations.filter((operation) => classifyOutcome(operation.net_pnl_base) === 'EVEN').length;

  return {
    operations_count: operations.length,
    win_count: usdStats.win_count,
    loss_count: usdStats.loss_count,
    even_count: evens,
    win_rate: usdStats.win_count + usdStats.loss_count > 0 ? Number((usdStats.win_count / (usdStats.win_count + usdStats.loss_count)).toFixed(8)) : null,
    trading_pnl_usd: sumPresent(operations.map((operation) => operation.net_pnl_usd)),
    avg_pnl_usd: meanValues(operations.map((operation) => operation.net_pnl_usd)),
    fees_usd: sumPresent(operations.map((operation) => operation.total_fees_usd)),
    gross_profit_usd: usdStats.gross_profit,
    gross_loss_usd_abs: usdStats.gross_loss_abs,
    average_winner_usd: usdStats.average_winner,
    average_loser_usd: usdStats.average_loser,
    profit_factor: usdStats.profit_factor,
    payoff_ratio: usdStats.payoff_ratio,
    expectancy_usd: usdStats.expectancy,
    gross_trading_result_usd: usdStats.gross_trading_result,
    fee_impact_pct: usdStats.fee_impact_pct,
    premium_captured_pct: premiumStats.premium_captured_pct,
    premium_captured_sample_count: premiumStats.premium_captured_sample_count,
    average_entry_dte: dteStats.average_entry_dte,
    average_exit_dte: dteStats.average_exit_dte,
    average_dte_reduction: dteStats.average_dte_reduction,
    by_currency: Object.entries(grouped)
      .sort(([left], [right]) => ['BTC', 'ETH', 'USDC'].indexOf(left) - ['BTC', 'ETH', 'USDC'].indexOf(right))
      .map(([currency, bucket]) => {
        const nativeStats = buildValueStats(bucket, 'net_pnl_base', 'total_fees_base');
        const currencyPremiumStats = buildPremiumStats(bucket);
        const pnlNative = sumPresent(bucket.map((operation) => operation.net_pnl_base), 12);
        const feesNative = sumPresent(bucket.map((operation) => operation.total_fees_base), 12);
        return {
          currency,
          pnl_native: pnlNative,
          avg_pnl_native: pnlNative !== null && bucket.length ? Number((pnlNative / bucket.length).toFixed(12)) : null,
          fees_native: feesNative,
          gross_profit_native: nativeStats.gross_profit,
          gross_loss_native_abs: nativeStats.gross_loss_abs,
          average_winner_native: nativeStats.average_winner,
          average_loser_native: nativeStats.average_loser,
          expectancy_native: nativeStats.expectancy,
          gross_trading_result_native: nativeStats.gross_trading_result,
          fee_impact_pct: nativeStats.fee_impact_pct,
          premium_captured_pct: currencyPremiumStats.premium_captured_pct,
          premium_captured_sample_count: currencyPremiumStats.premium_captured_sample_count,
        };
      }),
  };
};

const buildEmptyAnalyticsRow = (key, label) => ({
  key,
  label,
  sufficient_data: false,
  note: 'Not enough data',
  metrics: buildAnalyticsMetrics([]),
});

const matchesBucket = (value, min, max) => {
  if (max === null) return value >= min;
  if ((min === 0 && max === 7) || (min === 0 && max === 0.1) || (min === 1 && max === 3)) return value >= min && value <= max;
  return value >= min && value < max;
};

const deriveOperationEntryDeltaAbs = (operation) => {
  const legs = operationDetailsMap[operation.id]?.legs || [];
  let relevant = legs;
  if (operation.strategy_label === 'SHORT_STRANGLE' || operation.strategy_label === 'IRON_CONDOR') {
    relevant = legs.filter((leg) => leg.position_side === 'SHORT');
  }
  if (operation.strategy_label === 'LONG_CALL' || operation.strategy_label === 'LONG_PUT') {
    relevant = legs.filter((leg) => leg.position_side === 'LONG');
  }
  const deltas = relevant.map((leg) => leg.entry_delta).filter((value) => value !== null && value !== undefined).map((value) => Math.abs(Number(value)));
  if (!relevant.length || deltas.length !== relevant.length) return null;
  return Number((deltas.reduce((sum, value) => sum + value, 0) / deltas.length).toFixed(8));
};

const deriveOperationHoldDays = (operation) => {
  if (!operation.opened_at || !operation.closed_at) return null;
  return Number((((new Date(operation.closed_at).getTime() - new Date(operation.opened_at).getTime()) / 86400000)).toFixed(8));
};

const deriveOperationExitDte = (operation) => {
  if (!operation.closed_at || !operation.expiration) return null;
  return Number(Math.max((new Date(operation.expiration).getTime() - new Date(operation.closed_at).getTime()) / 86400000, 0).toFixed(8));
};

const deriveOperationPremiumCapture = (operation) => {
  const legs = operationDetailsMap[operation.id]?.legs || [];
  const eligible = legs.filter((leg) => leg.position_side === 'SHORT' && leg.status === 'CLOSED' && Number(leg.quantity_closed || 0) > 0);
  if (!eligible.length) {
    return { premium_received_base: null, premium_retained_base: null, premium_captured_pct: null };
  }

  const totals = eligible.reduce((acc, leg) => {
    const quantityOpened = Number(leg.quantity_opened || 0);
    const quantityClosed = Number(leg.quantity_closed || 0);
    const received = leg.average_entry_price !== null && leg.average_entry_price !== undefined
      ? quantityClosed * Number(leg.average_entry_price)
      : quantityOpened > 0
        ? (Number(leg.entry_notional_base || 0) / quantityOpened) * quantityClosed
        : Number(leg.entry_notional_base || 0);
    const paidToClose = Number(leg.exit_notional_base || 0);
    acc.received += received;
    acc.retained += received - paidToClose;
    return acc;
  }, { received: 0, retained: 0 });

  if (totals.received <= 0) {
    return { premium_received_base: null, premium_retained_base: null, premium_captured_pct: null };
  }

  return {
    premium_received_base: Number(totals.received.toFixed(12)),
    premium_retained_base: Number(totals.retained.toFixed(12)),
    premium_captured_pct: Number((totals.retained / totals.received).toFixed(8)),
  };
};

const groupRollEvents = (campaignId, rollLinks = []) => {
  const grouped = new Map();
  [...rollLinks]
    .sort((left, right) => new Date(left.created_at || 0).getTime() - new Date(right.created_at || 0).getTime())
    .forEach((link) => {
      const key = `${link.source_operation_id}__${link.target_operation_id}`;
      const current = grouped.get(key) || {
        campaign_id: campaignId,
        source_operation_id: link.source_operation_id,
        target_operation_id: link.target_operation_id,
        created_at: link.created_at,
        roll_types: new Set(),
      };
      (link.roll_types || []).forEach((rollType) => current.roll_types.add(rollType));
      grouped.set(key, current);
    });

  return Array.from(grouped.values()).map((event) => ({
    ...event,
    roll_types: Array.from(event.roll_types).sort(),
  }));
};

const computeStreaks = (operations) => {
  const ordered = [...operations].sort((left, right) => {
    const leftKey = `${left.closed_at || ''}-${left.id || ''}`;
    const rightKey = `${right.closed_at || ''}-${right.id || ''}`;
    return leftKey.localeCompare(rightKey);
  });

  let longestWin = 0;
  let longestLoss = 0;
  let currentType = null;
  let currentCount = 0;

  ordered.forEach((operation) => {
    const outcome = classifyOutcome(operation.net_pnl_base);
    if (outcome !== 'WIN' && outcome !== 'LOSS') {
      currentType = null;
      currentCount = 0;
      return;
    }

    if (outcome === currentType) {
      currentCount += 1;
    } else {
      currentType = outcome;
      currentCount = 1;
    }

    if (outcome === 'WIN') longestWin = Math.max(longestWin, currentCount);
    if (outcome === 'LOSS') longestLoss = Math.max(longestLoss, currentCount);
  });

  return {
    longest_win_streak: longestWin,
    longest_loss_streak: longestLoss,
  };
};

const buildTradeHighlight = (operation) => {
  if (!operation) return null;
  return {
    operation_id: operation.id,
    strategy_label: operation.strategy_label,
    underlying: operation.underlying,
    closed_at: operation.closed_at,
    net_pnl_base: operation.net_pnl_base,
    net_pnl_usd: operation.net_pnl_usd,
  };
};

const computeDemoMaxDrawdown = ({ bounds, currency = 'usd' }) => {
  const normalizedCurrency = String(currency || 'usd').toUpperCase();
  const startTime = bounds.startAt ? bounds.startAt.getTime() : Number.NEGATIVE_INFINITY;
  const endTime = bounds.endAt.getTime();

  let series = [];
  if (normalizedCurrency === 'USD') {
    const grouped = new Map();
    equityHistory
      .filter((row) => row.snapshot_timestamp_ms >= startTime && row.snapshot_timestamp_ms <= endTime)
      .forEach((row) => {
        const current = grouped.get(row.snapshot_timestamp_ms) || { native: 0, usd: 0 };
        current.native += Number(row.equity_usd || 0);
        current.usd += Number(row.equity_usd || 0);
        grouped.set(row.snapshot_timestamp_ms, current);
      });
    series = Array.from(grouped.entries()).sort(([left], [right]) => left - right).map(([timestamp, values]) => [timestamp, values.native, values.usd]);
  } else {
    series = equityHistory
      .filter((row) => row.currency === normalizedCurrency && row.snapshot_timestamp_ms >= startTime && row.snapshot_timestamp_ms <= endTime)
      .sort((left, right) => left.snapshot_timestamp_ms - right.snapshot_timestamp_ms)
      .map((row) => [row.snapshot_timestamp_ms, Number(row.equity_native || 0), row.equity_usd !== null && row.equity_usd !== undefined ? Number(row.equity_usd) : null]);
  }

  if (series.length < 2) {
    return { max_drawdown_pct: null, max_drawdown_native: null, max_drawdown_usd: null };
  }

  let peakValue = series[0][1];
  let peakUsd = series[0][2];
  let maxDrawdownPct = null;
  let maxDrawdownNative = null;
  let maxDrawdownUsd = null;

  series.forEach(([, currentValue, currentUsd]) => {
    if (currentValue > peakValue) {
      peakValue = currentValue;
      peakUsd = currentUsd;
      return;
    }
    if (peakValue <= 0) return;

    const drawdownNative = currentValue - peakValue;
    const drawdownPct = drawdownNative / peakValue;
    if (maxDrawdownPct === null || drawdownPct < maxDrawdownPct) {
      maxDrawdownPct = Number(drawdownPct.toFixed(8));
      maxDrawdownNative = Number(drawdownNative.toFixed(8));
      maxDrawdownUsd = currentUsd !== null && currentUsd !== undefined && peakUsd !== null && peakUsd !== undefined
        ? Number((currentUsd - peakUsd).toFixed(8))
        : null;
    }
  });

  return {
    max_drawdown_pct: maxDrawdownPct,
    max_drawdown_native: maxDrawdownNative,
    max_drawdown_usd: maxDrawdownUsd,
  };
};

const buildAnalyticsPayload = ({ range = '30d', start = null, end = null, currency = 'usd', underlying = 'all', strategy = 'all' } = {}) => {
  const bounds = rangeBounds({ range, start, end });
  const filteredClosedOperations = operationList
    .filter((operation) => analyticsClosedOperationInRange(operation, bounds))
    .filter((operation) => analyticsMatchesFilters(operation, underlying, strategy))
    .map((operation) => ({
      ...operation,
      entry_delta_abs: deriveOperationEntryDeltaAbs(operation),
      hold_days: deriveOperationHoldDays(operation),
      exit_dte: deriveOperationExitDte(operation),
      dte_reduction: operation.dte_at_entry !== null && operation.dte_at_entry !== undefined && deriveOperationExitDte(operation) !== null
        ? Number((Number(operation.dte_at_entry) - Number(deriveOperationExitDte(operation))).toFixed(8))
        : null,
      ...deriveOperationPremiumCapture(operation),
    }));

  const strategyRows = ['SHORT_STRANGLE', 'IRON_CONDOR', 'LONG_CALL', 'LONG_PUT'].map((strategyLabel) => {
    const bucket = filteredClosedOperations.filter((operation) => operation.strategy_label === strategyLabel);
    return bucket.length
      ? { key: strategyLabel.toLowerCase(), label: strategyLabel, sufficient_data: true, note: null, metrics: buildAnalyticsMetrics(bucket) }
      : buildEmptyAnalyticsRow(strategyLabel.toLowerCase(), strategyLabel);
  });

  const buildBucketRows = (buckets, getter) => buckets.map((bucket) => {
    const items = filteredClosedOperations.filter((operation) => getter(operation) !== null && getter(operation) !== undefined && matchesBucket(Number(getter(operation)), bucket.min, bucket.max));
    return items.length
      ? { key: bucket.key, label: bucket.label, sufficient_data: true, note: null, metrics: buildAnalyticsMetrics(items) }
      : buildEmptyAnalyticsRow(bucket.key, bucket.label);
  });

  const filteredCampaigns = campaignSummaries.filter((campaign) => {
    const openedAt = new Date(campaign.opened_at);
    const inRange = (!bounds.startAt || openedAt >= bounds.startAt) && openedAt <= bounds.endAt;
    return inRange && analyticsMatchesFilters(campaign, underlying, strategy, 'strategy_origin');
  });

  let totalRolls = 0;
  const preRollOperations = [];
  const postRollCycleItems = [];
  let improvedCycles = 0;
  let worsenedCycles = 0;
  let recoveredCycles = 0;
  let partiallyRecoveredCycles = 0;
  let notRecoveredCycles = 0;
  const pseudoCycleOperations = [];

  const cycleTimelines = filteredCampaigns
    .sort((left, right) => new Date(right.opened_at).getTime() - new Date(left.opened_at).getTime())
    .flatMap((campaign) => {
      const detail = campaignDetailsMap[campaign.id];
      const events = groupRollEvents(campaign.id, detail?.roll_links || []);
      totalRolls += events.length;

      const sourceOperations = [];
      const seenSourceIds = new Set();
      events.forEach((event) => {
        const operation = operationList.find((item) => item.id === event.source_operation_id) || null;
        if (operation && !seenSourceIds.has(operation.id)) {
          seenSourceIds.add(operation.id);
          sourceOperations.push(operation);
          preRollOperations.push(operation);
        }
      });

      const memberOperations = [...(detail?.operations || [])].sort((left, right) => new Date(left.opened_at).getTime() - new Date(right.opened_at).getTime());
      const sourceIds = new Set(sourceOperations.map((operation) => operation.id));
      const postRollOperations = memberOperations.filter((operation) => !sourceIds.has(operation.id));
      const preRollTotalBase = sumPresent(sourceOperations.map((operation) => operation.net_pnl_base), 12);
      const preRollTotalUsd = sumPresent(sourceOperations.map((operation) => operation.net_pnl_usd));
      const postRollTotalBase = sumPresent(postRollOperations.map((operation) => operation.net_pnl_base), 12);
      const postRollTotalUsd = sumPresent(postRollOperations.map((operation) => operation.net_pnl_usd));

      let recoveryStatus = null;
      if (events.length && preRollTotalBase !== null && campaign.campaign_net_pnl_base !== null && campaign.campaign_net_pnl_base !== undefined) {
        if (Number(campaign.campaign_net_pnl_base) > Number(preRollTotalBase)) improvedCycles += 1;
        if (Number(campaign.campaign_net_pnl_base) < Number(preRollTotalBase)) worsenedCycles += 1;

        if (Number(preRollTotalBase) < 0) {
          if (Number(campaign.campaign_net_pnl_base) > 0) {
            recoveryStatus = 'RECOVERED';
            recoveredCycles += 1;
          } else if (Number(campaign.campaign_net_pnl_base) > Number(preRollTotalBase)) {
            recoveryStatus = 'PARTIALLY_RECOVERED';
            partiallyRecoveredCycles += 1;
          } else {
            recoveryStatus = 'NOT_RECOVERED';
            notRecoveredCycles += 1;
          }
        }
      }

      if (postRollTotalBase !== null) {
        postRollCycleItems.push({
          underlying: campaign.underlying,
          net_pnl_base: postRollTotalBase,
          net_pnl_usd: postRollTotalUsd,
          total_fees_base: 0,
          total_fees_usd: 0,
        });
      }

      pseudoCycleOperations.push({
        id: campaign.id,
        underlying: campaign.underlying,
        net_pnl_base: campaign.campaign_net_pnl_base,
        net_pnl_usd: campaign.campaign_net_pnl_usd,
        total_fees_base: campaign.total_fees_base,
        total_fees_usd: campaign.total_fees_usd,
        roll_count: events.length,
      });

      if (!(events.length || campaign.operation_count > 1)) return [];

      const items = [];
      const rollEventMap = new Set(events.map((event) => `${event.source_operation_id}__${event.target_operation_id}`));
      memberOperations.forEach((operation, index) => {
        items.push({
          type: 'operation',
          label: operation.expiration_label,
          operation_id: operation.id,
          status: operation.status,
          expiration_label: operation.expiration_label,
          opened_at: operation.opened_at,
          closed_at: operation.closed_at,
          pnl_base: operation.net_pnl_base,
          pnl_usd: operation.net_pnl_usd,
        });
        const nextOperation = memberOperations[index + 1];
        if (nextOperation && rollEventMap.has(`${operation.id}__${nextOperation.id}`)) {
          items.push({ type: 'roll', label: 'ROLL', created_at: nextOperation.opened_at });
        }
      });

      return [{
        campaign_id: campaign.id,
        underlying: campaign.underlying,
        strategy_label: campaign.strategy_origin,
        status: campaign.status,
        operation_count: campaign.operation_count,
        roll_count: events.length,
        pre_roll_pnl_base: preRollTotalBase,
        pre_roll_pnl_usd: preRollTotalUsd,
        post_roll_pnl_base: postRollTotalBase,
        post_roll_pnl_usd: postRollTotalUsd,
        recovery_status: recoveryStatus,
        campaign_net_pnl_base: campaign.campaign_net_pnl_base,
        campaign_net_pnl_usd: campaign.campaign_net_pnl_usd,
        items,
      }];
    })
    .slice(0, 12);

  const cycleMetrics = buildAnalyticsMetrics(pseudoCycleOperations);
  const preRollMetrics = buildAnalyticsMetrics(preRollOperations);
  const postRollMetrics = buildAnalyticsMetrics(postRollCycleItems);
  const overviewMetrics = buildAnalyticsMetrics(filteredClosedOperations);
  const streaks = computeStreaks(filteredClosedOperations);
  const sameUnderlying = new Set(filteredClosedOperations.map((operation) => String(operation.underlying || '').toUpperCase()).filter(Boolean));
  const compareByNative = sameUnderlying.size === 1 && String(currency || 'usd').toLowerCase() !== 'usd';
  const bestTrade = filteredClosedOperations.length
    ? filteredClosedOperations.reduce((best, operation) => {
      if (!best) return operation;
      const left = compareByNative ? Number(best.net_pnl_base ?? Number.NEGATIVE_INFINITY) : Number(best.net_pnl_usd ?? Number.NEGATIVE_INFINITY);
      const right = compareByNative ? Number(operation.net_pnl_base ?? Number.NEGATIVE_INFINITY) : Number(operation.net_pnl_usd ?? Number.NEGATIVE_INFINITY);
      return right > left ? operation : best;
    }, null)
    : null;
  const worstTrade = filteredClosedOperations.length
    ? filteredClosedOperations.reduce((worst, operation) => {
      if (!worst) return operation;
      const left = compareByNative ? Number(worst.net_pnl_base ?? Number.POSITIVE_INFINITY) : Number(worst.net_pnl_usd ?? Number.POSITIVE_INFINITY);
      const right = compareByNative ? Number(operation.net_pnl_base ?? Number.POSITIVE_INFINITY) : Number(operation.net_pnl_usd ?? Number.POSITIVE_INFINITY);
      return right < left ? operation : worst;
    }, null)
    : null;
  const maxDrawdown = computeDemoMaxDrawdown({ bounds, currency });

  const buildRollBucketRows = () => {
    const buckets = [
      { key: 'rolls_0', label: '0 rolls', min: 0, max: 0 },
      { key: 'rolls_1', label: '1 roll', min: 1, max: 1 },
      { key: 'rolls_2', label: '2 rolls', min: 2, max: 2 },
      { key: 'rolls_3_plus', label: '3+ rolls', min: 3, max: null },
    ];
    return buckets.map((bucket) => {
      const items = pseudoCycleOperations.filter((campaign) => {
        const rollCount = Number(campaign.roll_count || 0);
        return bucket.max === null ? rollCount >= bucket.min : rollCount >= bucket.min && rollCount <= bucket.max;
      });
      return items.length
        ? { key: bucket.key, label: bucket.label, sufficient_data: true, note: null, metrics: buildAnalyticsMetrics(items) }
        : buildEmptyAnalyticsRow(bucket.key, bucket.label);
    });
  };

  return {
    range: buildRangeMeta({ range, start, end }),
    currency_filter: currency,
    underlying_filter: underlying,
    strategy_filter: strategy,
    overview: {
      total_operations: filteredClosedOperations.length,
      metrics: overviewMetrics,
      cycle_pnl_usd: cycleMetrics.trading_pnl_usd,
      cycle_pnl_by_currency: cycleMetrics.by_currency,
    },
    performance_quality: {
      metrics: overviewMetrics,
      max_drawdown_pct: maxDrawdown.max_drawdown_pct,
      max_drawdown_native: maxDrawdown.max_drawdown_native,
      max_drawdown_usd: maxDrawdown.max_drawdown_usd,
      longest_win_streak: streaks.longest_win_streak,
      longest_loss_streak: streaks.longest_loss_streak,
      best_trade: buildTradeHighlight(bestTrade),
      worst_trade: buildTradeHighlight(worstTrade),
    },
    strategy_rows: strategyRows,
    entry_dte_rows: buildBucketRows(DTE_BUCKETS, (operation) => operation.dte_at_entry),
    entry_delta_rows: buildBucketRows(DELTA_BUCKETS, (operation) => operation.entry_delta_abs),
    underlying_rows: ['BTC', 'ETH', 'USDC']
      .filter((code) => filteredClosedOperations.some((operation) => operation.underlying === code))
      .map((currencyCode) => {
        const bucket = filteredClosedOperations.filter((operation) => operation.underlying === currencyCode);
        return { key: currencyCode.toLowerCase(), label: currencyCode, underlying: currencyCode, sufficient_data: true, note: null, metrics: buildAnalyticsMetrics(bucket) };
      }),
    duration_rows: buildBucketRows(DURATION_BUCKETS, (operation) => operation.hold_days),
    cycle_summary: {
      total_cycles: filteredCampaigns.length,
      total_rolls: totalRolls,
      cycle_win_rate: cycleMetrics.win_rate,
      average_rolls_per_cycle: filteredCampaigns.length ? Number((totalRolls / filteredCampaigns.length).toFixed(8)) : null,
      pre_roll_pnl_usd: preRollMetrics.trading_pnl_usd,
      pre_roll_pnl_by_currency: preRollMetrics.by_currency,
      post_roll_pnl_usd: postRollMetrics.trading_pnl_usd,
      post_roll_pnl_by_currency: postRollMetrics.by_currency,
      final_cycle_pnl_usd: cycleMetrics.trading_pnl_usd,
      final_cycle_pnl_by_currency: cycleMetrics.by_currency,
      positive_cycles: filteredCampaigns.filter((campaign) => Number(campaign.campaign_net_pnl_base || 0) > 0).length,
      negative_cycles: filteredCampaigns.filter((campaign) => Number(campaign.campaign_net_pnl_base || 0) < 0).length,
      improved_cycles: improvedCycles,
      worsened_cycles: worsenedCycles,
      recovered_cycles: recoveredCycles,
      partially_recovered_cycles: partiallyRecoveredCycles,
      not_recovered_cycles: notRecoveredCycles,
      roll_bucket_rows: buildRollBucketRows(),
    },
    cycle_timelines: cycleTimelines,
  };
};

const buildTradingSummaryForCurrency = (currency, bounds) => {
  const operations = operationList.filter((item) => item.underlying === currency && operationOverlapsRange(item, bounds));
  const realized = Number(operations.reduce((sum, item) => sum + Number(item.realized_pnl_base || 0), 0).toFixed(8));
  const unrealized = Number(operations.reduce((sum, item) => sum + Number(item.unrealized_pnl_base || 0), 0).toFixed(8));
  const fees = Number(operations.reduce((sum, item) => sum + Number(item.total_fees_base || 0), 0).toFixed(8));
  const net = Number((realized + unrealized - fees).toFixed(8));
  const flows = cashFlowEvents.filter((item) => item.currency === currency && (!bounds.startAt || item.timestamp_ms >= bounds.startAt.getTime()) && item.timestamp_ms <= bounds.endAt.getTime());
  const externalCashFlowNative = Number(flows.reduce((sum, item) => sum + Number(item.amount_native || 0), 0).toFixed(8));

  return {
    currency,
    starting_equity_native: null,
    starting_equity_usd: null,
    ending_equity_native: currency === 'BTC' ? 2.1485 : 20.47,
    ending_equity_usd: currency === 'BTC' ? toUsd(2.1485, 'BTC') : toUsd(20.47, 'ETH'),
    ending_usd_price: CURRENT_PRICES[currency],
    realized_pnl_native: realized,
    realized_pnl_usd: toUsd(realized, currency),
    unrealized_pnl_native: unrealized,
    unrealized_pnl_usd: toUsd(unrealized, currency),
    net_pnl_native: net,
    net_pnl_usd: toUsd(net, currency),
    fees_native: fees,
    fees_usd: toUsd(fees, currency),
    external_cash_flow_native: externalCashFlowNative,
    external_cash_flow_usd: toUsd(externalCashFlowNative, currency),
    external_cash_flow_event_count: flows.length,
    closed_operations_count: operations.filter((item) => item.status === 'CLOSED').length,
    open_operations_count: operations.filter((item) => item.status === 'OPEN').length,
    closed_campaigns_count: campaignSummaries.filter((item) => item.underlying === currency && item.status === 'CLOSED').length,
    open_campaigns_count: campaignSummaries.filter((item) => item.underlying === currency && item.status === 'OPEN').length,
  };
};

const buildPerformancePayload = ({ range = '30d', start = null, end = null }) => {
  const bounds = rangeBounds({ range, start, end });
  const currencies = ['BTC', 'ETH'].map((currency) => buildTradingSummaryForCurrency(currency, bounds));
  const totalsUsd = {
    starting_equity_usd: null,
    ending_equity_usd: Number(currencies.reduce((sum, item) => sum + Number(item.ending_equity_usd || 0), 0).toFixed(8)),
    realized_pnl_usd: Number(currencies.reduce((sum, item) => sum + Number(item.realized_pnl_usd || 0), 0).toFixed(8)),
    unrealized_pnl_usd: Number(currencies.reduce((sum, item) => sum + Number(item.unrealized_pnl_usd || 0), 0).toFixed(8)),
    net_pnl_usd: Number(currencies.reduce((sum, item) => sum + Number(item.net_pnl_usd || 0), 0).toFixed(8)),
    fees_usd: Number(currencies.reduce((sum, item) => sum + Number(item.fees_usd || 0), 0).toFixed(8)),
    external_cash_flow_usd: Number(currencies.reduce((sum, item) => sum + Number(item.external_cash_flow_usd || 0), 0).toFixed(8)),
    closed_operations_count: currencies.reduce((sum, item) => sum + Number(item.closed_operations_count || 0), 0),
    open_operations_count: currencies.reduce((sum, item) => sum + Number(item.open_operations_count || 0), 0),
    closed_campaigns_count: currencies.reduce((sum, item) => sum + Number(item.closed_campaigns_count || 0), 0),
    open_campaigns_count: currencies.reduce((sum, item) => sum + Number(item.open_campaigns_count || 0), 0),
  };

  const hasAnyCashFlow = currencies.some((item) => item.external_cash_flow_event_count > 0);

  return {
    range: buildRangeMeta({ range, start, end }),
    price_source: 'GENI Options demo dataset',
    currencies,
    totals_usd: totalsUsd,
    cash_flow_analysis: {
      available: true,
      reason: null,
      by_currency: currencies.map((item) => ({
        currency: item.currency,
        external_cash_flow_native: item.external_cash_flow_native,
        external_cash_flow_usd: item.external_cash_flow_usd,
        external_cash_flow_event_count: item.external_cash_flow_event_count,
        external_cash_flow_types: cashFlowEvents.filter((flow) => flow.currency === item.currency).map((flow) => flow.type),
      })),
      total_external_cash_flow_usd: totalsUsd.external_cash_flow_usd,
    },
    return_pct: hasAnyCashFlow ? null : 0.0241,
    return_pct_reason: hasAnyCashFlow ? 'Return unavailable across multiple capital periods' : null,
    equity_history_coverage_complete: true,
    unrealized_pnl_current_only: true,
  };
};

const buildCurrentCapitalPeriodView = (periods) => {
  const current = periods.find((item) => item.status === 'OPEN');
  if (!current) return null;
  return {
    ...current,
    current_equity_native: current.current_equity_native ?? current.ending_equity_native,
    current_equity_usd: current.current_equity_usd ?? current.ending_equity_usd,
    current_return_pct: current.current_return_pct ?? current.return_pct,
    current_return_pct_reason: current.current_return_pct_reason ?? current.return_pct_reason,
  };
};

const buildDemoProjectionPoints = ({ currentEquity, annualizedReturnPct }) => {
  const growthFactor = 1 + Number(annualizedReturnPct || 0);
  return Array.from({ length: 13 }, (_, index) => ({
    label: index === 0 ? 'today' : `${index}m`,
    month_index: index,
    equity_native: Number((Number(currentEquity) * Math.pow(growthFactor, index / 12)).toFixed(12)),
  }));
};

const buildDemoEquityProjection = (currency) => {
  if (currency === 'BTC') {
    const currentEquity = 2.1485;
    const annualizedReturnPct = 0.184;
    const projectedPoints = buildDemoProjectionPoints({ currentEquity, annualizedReturnPct });
    return {
      status: 'ready',
      asset: 'BTC',
      current_equity: currentEquity,
      available_history_days: 365,
      source_window: '12m',
      source_window_label: '12m',
      source_start: daysAgo(365),
      source_end: daysAgo(0),
      source_days: 365,
      source_return_pct: annualizedReturnPct,
      annualized_return_pct: annualizedReturnPct,
      projected_12m_equity: projectedPoints[12].equity_native,
      projected_points: projectedPoints,
      methodology: 'capital_period_compounded_return',
      limited_history: false,
      insufficient_reason: null,
    };
  }

  const currentEquity = 20.47;
  const annualizedReturnPct = -0.124;
  const projectedPoints = buildDemoProjectionPoints({ currentEquity, annualizedReturnPct });
  return {
    status: 'ready',
    asset: 'ETH',
    current_equity: currentEquity,
    available_history_days: 94,
    source_window: '3m',
    source_window_label: '3m',
    source_start: daysAgo(94),
    source_end: daysAgo(0),
    source_days: 94,
    source_return_pct: -0.03165214,
    annualized_return_pct: annualizedReturnPct,
    projected_12m_equity: projectedPoints[12].equity_native,
    projected_points: projectedPoints,
    methodology: 'capital_period_compounded_return',
    limited_history: true,
    insufficient_reason: null,
  };
};

const buildCapitalPeriodsPayload = ({ range = 'all', currency = null, start = null, end = null }) => {
  const bounds = rangeBounds({ range, start, end });
  const rangeMeta = buildRangeMeta({ range, start, end });
  const allByCurrency = {
    BTC: btcPeriods,
    ETH: ethPeriods,
  };

  const selectedCurrencies = currency ? [currency.toUpperCase()] : ['BTC', 'ETH'];
  const currencies = selectedCurrencies.map((code) => {
    const periods = allByCurrency[code] || [];
    const visiblePeriods = filterByRange(periods, (item) => item.started_at, (item) => item.ended_at || new Date().toISOString(), bounds);
    const visibleFlows = cashFlowEvents.filter((item) => item.currency === code && (!bounds.startAt || item.timestamp_ms >= bounds.startAt.getTime()) && item.timestamp_ms <= bounds.endAt.getTime());
    const currentPeriod = buildCurrentCapitalPeriodView(periods);
    const intersectsMultiple = visiblePeriods.length > 1;
    const singlePeriod = visiblePeriods.length === 1 ? visiblePeriods[0] : null;
    const rangeReturnPct = singlePeriod && !intersectsMultiple ? singlePeriod.return_pct : null;

    return {
      currency: code,
      periods: visiblePeriods,
      current_period: currentPeriod,
      cash_flow_markers: visibleFlows,
      range_crosses_multiple_periods: intersectsMultiple,
      range_return_pct: rangeReturnPct,
      range_return_pct_reason: intersectsMultiple ? 'Return unavailable across multiple capital periods' : (rangeReturnPct === null ? 'Return unavailable for this range' : null),
      range_return_native: singlePeriod ? Number(((singlePeriod.ending_equity_native || 0) - (singlePeriod.starting_equity_native || 0)).toFixed(12)) : null,
      range_starting_equity_native: singlePeriod?.starting_equity_native ?? null,
      range_ending_equity_native: singlePeriod?.ending_equity_native ?? currentPeriod?.current_equity_native ?? null,
      twr_implemented: false,
      twr_pct: null,
      twr_reason: 'Combined return across capital periods coming later',
      absolute_trading_metrics: buildTradingSummaryForCurrency(code, bounds),
    };
  });

  const components = currencies.map((item) => {
    const nativeReturnAmount = item.range_return_native;
    const referencePrice = CURRENT_PRICES[item.currency] ?? null;
    if (item.range_return_pct === null || item.range_return_pct === undefined || nativeReturnAmount === null || nativeReturnAmount === undefined) {
      return {
        asset: item.currency,
        return_pct: item.range_return_pct,
        return_pct_reason: item.range_return_pct_reason,
        native_return_amount: nativeReturnAmount,
        reference_price: null,
        reference_price_status: 'not_requested',
        reference_price_captured_at: null,
        usd_equivalent: null,
        included_in_total: false,
        status: 'return_unavailable',
        exclusion_reason: 'return_unavailable',
      };
    }

    return {
      asset: item.currency,
      return_pct: item.range_return_pct,
      return_pct_reason: item.range_return_pct_reason,
      native_return_amount: nativeReturnAmount,
      reference_price: referencePrice,
      reference_price_status: 'fresh',
      reference_price_captured_at: daysAgo(0),
      usd_equivalent: Number((nativeReturnAmount * referencePrice).toFixed(8)),
      included_in_total: true,
      status: 'complete',
      exclusion_reason: null,
    };
  });

  const includedComponents = components.filter((item) => item.included_in_total);
  const approxReturnUsd = includedComponents.length
    ? {
      value: Number(includedComponents.reduce((sum, item) => sum + Number(item.usd_equivalent || 0), 0).toFixed(8)),
      status: 'complete',
      is_partial: false,
      current_price_updated_at: daysAgo(0),
      included_assets: includedComponents.map((item) => item.asset),
      missing_price_assets: [],
      omitted_assets_no_return: components.filter((item) => item.status === 'return_unavailable').map((item) => item.asset),
      components,
    }
    : {
      value: null,
      status: 'unavailable',
      is_partial: false,
      current_price_updated_at: null,
      included_assets: [],
      missing_price_assets: [],
      omitted_assets_no_return: components.filter((item) => item.status === 'return_unavailable').map((item) => item.asset),
      components,
    };

  const consolidatedStartingUsd = currencies.reduce((sum, item) => {
    const period = item.periods?.[0] || item.current_period;
    const usd = period?.starting_equity_usd;
    return usd !== null && usd !== undefined ? sum + Number(usd) : sum;
  }, 0);
  const consolidatedEndingUsd = currencies.reduce((sum, item) => {
    const period = item.current_period;
    const usd = period?.ending_equity_usd ?? period?.current_equity_usd;
    return usd !== null && usd !== undefined ? sum + Number(usd) : sum;
  }, 0);
  const accountReturn = consolidatedStartingUsd > 0
    ? {
      return_pct: Number(((consolidatedEndingUsd - consolidatedStartingUsd) / consolidatedStartingUsd).toFixed(12)),
      return_pct_reason: null,
      starting_equity_usd: Number(consolidatedStartingUsd.toFixed(8)),
      ending_equity_usd: Number(consolidatedEndingUsd.toFixed(8)),
      external_cash_flow_usd: 0,
      external_cash_flow_event_count: 0,
      pnl_usd: Number((consolidatedEndingUsd - consolidatedStartingUsd).toFixed(8)),
    }
    : {
      return_pct: null,
      return_pct_reason: 'Equity inicial não positiva; retorno percentual do intervalo não é matematicamente confiável.',
      starting_equity_usd: null,
      ending_equity_usd: consolidatedEndingUsd || null,
      external_cash_flow_usd: null,
      external_cash_flow_event_count: 0,
      pnl_usd: null,
    };

  return {
    range: rangeMeta,
    approx_return_usd: approxReturnUsd,
    account_return: accountReturn,
    currencies,
  };
};

const buildOverviewPayload = () => ({
  connected: true,
  environment: 'demo',
  sync_status: 'read_only',
  sync_error: null,
  connected_at: daysAgo(180),
  last_sync_at: daysAgo(1),
  permissions: ['read_only_demo'],
  access_mode: 'demo_portfolio',
  can_manage_connection: false,
  can_sync: false,
  portfolio_label: 'DEMO PORTFOLIO',
  managed_connection_label: 'DEMONSTRATIVE DATASET',
  account_summaries: currentAccountSummaries,
  portfolio_risk_demo: portfolioRiskDemo,
  positions: [],
  recent_executions: [],
  totals: {
    currencies_synced: 2,
    open_positions: operationList.filter((item) => item.status === 'OPEN').length,
    stored_executions: 0,
    stored_legs: operationSeeds.reduce((sum, item) => sum + item.detail.legs.length, 0),
    stored_operations: operationList.length,
    stored_campaigns: campaignSummaries.length,
    stored_roll_links: Object.values(campaignDetailsMap).reduce((sum, item) => sum + item.roll_links.length, 0),
    stored_roll_review_items: 0,
    stored_equity_snapshots: equityHistory.length,
  },
});

const buildAccountPayload = () => {
  const performanceAll = buildPerformancePayload({ range: 'all' });
  const currencies = [
    {
      account_key: ACCOUNT_KEY,
      currency: 'BTC',
      snapshot_at: daysAgo(0),
      snapshot_timestamp_ms: toMs(daysAgo(0)),
      usd_index_name: 'btc_usd',
      usd_price: CURRENT_PRICES.BTC,
      usd_price_source: 'GENI Options demo dataset',
      usd_price_source_field: 'demo_mark',
      equity_native: 2.1485,
      equity_usd: toUsd(2.1485, 'BTC'),
      balance_native: 2.0626,
      available_funds_native: 1.5909,
      available_withdrawal_funds_native: 1.4609,
      margin_balance_native: 1.762,
      initial_margin_native: 0.3102,
      maintenance_margin_native: 0.2121,
      close_out_margin_native: 0.3867,
      projected_initial_margin_native: 0.3518,
      projected_maintenance_margin_native: 0.2488,
      projected_close_out_margin_native: 0.4297,
      options_value_native: 0.4727,
      options_pl_native: 0.0215,
      options_session_upl_native: 0.0086,
      options_session_rpl_native: -0.0043,
      futures_pl_native: 0,
      futures_session_upl_native: 0,
      futures_session_rpl_native: 0,
      session_upl_native: 0.0086,
      session_rpl_native: -0.0043,
      total_pl_native: 0.0043,
      fee_balance_native: 0.0172,
      locked_balance_native: 0.1074,
      spot_reserve_native: 0,
      additional_reserve_native: 0,
      unrealized_pnl_native: 0.0086,
      unrealized_pnl_usd: toUsd(0.0086, 'BTC'),
      unrealized_pnl_source_field: 'demo_mark',
      realized_pnl_native: -0.0043,
      realized_pnl_usd: toUsd(-0.0043, 'BTC'),
      realized_pnl_source_field: 'demo_mark',
      options_delta: 0.47,
      options_gamma: 0,
      options_vega: 0.027,
      options_theta: 0.0021,
      delta_total: 0.47,
    },
    {
      account_key: ACCOUNT_KEY,
      currency: 'ETH',
      snapshot_at: daysAgo(0),
      snapshot_timestamp_ms: toMs(daysAgo(0)),
      usd_index_name: 'eth_usd',
      usd_price: CURRENT_PRICES.ETH,
      usd_price_source: 'GENI Options demo dataset',
      usd_price_source_field: 'demo_mark',
      equity_native: 20.47,
      equity_usd: toUsd(20.47, 'ETH'),
      balance_native: 19.65,
      available_funds_native: 15.14,
      available_withdrawal_funds_native: 13.91,
      margin_balance_native: 16.79,
      initial_margin_native: 2.91,
      maintenance_margin_native: 2.05,
      close_out_margin_native: 3.68,
      projected_initial_margin_native: 3.31,
      projected_maintenance_margin_native: 2.46,
      projected_close_out_margin_native: 4.08,
      options_value_native: 4.51,
      options_pl_native: 0.2,
      options_session_upl_native: 0.08,
      options_session_rpl_native: -0.04,
      futures_pl_native: 0,
      futures_session_upl_native: 0,
      futures_session_rpl_native: 0,
      session_upl_native: 0.08,
      session_rpl_native: -0.04,
      total_pl_native: 0.04,
      fee_balance_native: 0.16,
      locked_balance_native: 1.02,
      spot_reserve_native: 0,
      additional_reserve_native: 0,
      unrealized_pnl_native: 0.08,
      unrealized_pnl_usd: toUsd(0.08, 'ETH'),
      unrealized_pnl_source_field: 'demo_mark',
      realized_pnl_native: -0.04,
      realized_pnl_usd: toUsd(-0.04, 'ETH'),
      realized_pnl_source_field: 'demo_mark',
      options_delta: 0.04,
      options_gamma: 0,
      options_vega: -0.16,
      options_theta: 0.0027,
      delta_total: 0.04,
    },
  ];

  return {
    connected: true,
    environment: 'demo',
    sync_status: 'read_only',
    sync_error: null,
    connected_at: daysAgo(180),
    last_sync_at: daysAgo(1),
    permissions: ['read_only_demo'],
    access_mode: 'demo_portfolio',
    can_manage_connection: false,
    can_sync: false,
    portfolio_label: 'DEMO PORTFOLIO',
    managed_connection_label: 'DEMONSTRATIVE DATASET',
    portfolio_risk_demo: portfolioRiskDemo,
    price_source: 'GENI Options demo dataset',
    account_equity: {
      snapshot_at: daysAgo(0),
      total_account_value_usd: Number((toUsd(2.1485, 'BTC') + toUsd(20.47, 'ETH')).toFixed(8)),
      currencies,
    },
    trading_pnl: {
      by_currency: performanceAll.currencies,
      totals_usd: performanceAll.totals_usd,
    },
  };
};

const buildOperationsPayload = (limit = 100) => {
  const items = [...operationList].sort((a, b) => new Date(b.closed_at || b.opened_at).getTime() - new Date(a.closed_at || a.opened_at).getTime());
  return {
    items: items.slice(0, limit),
    total: items.length,
    limit,
    grouping_window_minutes: 30,
    classifier_version: 'demo-fixture-v1',
  };
};

const DEMO_ALERTS_STORAGE_KEY = 'geni-options-demo-alerts-v1';

const alertDefinitions = [
  { id: 'dte_roll_window', availability: 'implemented', supported_scopes: ['strategy_default', 'operation'], reason: null },
  { id: 'iv_rank', availability: 'data_blocked', supported_scopes: ['underlying'], reason: 'IV history is not available in the current demo dataset.' },
  { id: 'iv_percentile', availability: 'data_blocked', supported_scopes: ['underlying'], reason: 'IV history is not available in the current demo dataset.' },
  { id: 'leg_delta', availability: 'implemented', supported_scopes: ['strategy_default', 'operation'], reason: null },
  { id: 'position_exposure', availability: 'implemented', supported_scopes: ['underlying'], reason: null },
  { id: 'profit_target', availability: 'implemented', supported_scopes: ['operation'], reason: null },
  { id: 'premium_captured', availability: 'data_blocked', supported_scopes: ['operation', 'strategy_default'], reason: 'Premium Captured is not available as a live operation metric in the demo alerts engine.' },
  { id: 'max_loss', availability: 'implemented', supported_scopes: ['operation'], reason: null },
  { id: 'strike_proximity', availability: 'implemented', supported_scopes: ['operation'], reason: null },
  { id: 'breakeven_proximity', availability: 'data_blocked', supported_scopes: ['operation'], reason: 'Breakeven is not calculated safely in the current dataset.' },
  { id: 'margin_risk', availability: 'implemented', supported_scopes: ['account'], reason: null },
];

const buildDemoAlertTargets = () => ({
  operations: operationList.filter((item) => item.status === 'OPEN').map((item) => ({
    id: item.id,
    title: `${item.underlying} · ${item.strategy_label}`,
    underlying: item.underlying,
    strategy_label: item.strategy_label,
    expiration_label: item.expiration_label,
    dte: item.current_dte,
    active_alert_count: 0,
  })),
  strategies: Array.from(new Set(operationList.filter((item) => item.status === 'OPEN').map((item) => item.strategy_label))).sort(),
  underlyings: ['BTC', 'ETH'],
  margin_currencies: ['BTC', 'ETH'],
});

const buildDefaultDemoAlertsState = () => ({
  preferences: {
    email_enabled: true,
    locale: 'ptBR',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    email: 'demo@geni.app',
    provider_ready: false,
  },
  rules: [
    {
      id: 'demo-alert-1',
      enabled: true,
      scope_type: 'operation',
      scope_id: 'op_btc_short_strangle_sep',
      operation_id: 'op_btc_short_strangle_sep',
      strategy_label: 'SHORT_STRANGLE',
      underlying: 'BTC',
      alert_type: 'dte_roll_window',
      channels: { in_app: true, email: true },
      condition: { threshold_value: 21 },
      cooldown_seconds: 86400,
      metadata: {},
      state: 'active',
      active_trigger_count: 0,
      condition_summary: 'DTE <= 21',
      scope_label: 'BTC · SHORT_STRANGLE',
      availability: 'implemented',
      availability_reason: null,
      last_evaluated_at: daysAgo(0),
      last_triggered_at: null,
      operation_title: 'BTC · SHORT_STRANGLE',
      operation_expiration_label: '27 SEP 2026',
    },
    {
      id: 'demo-alert-2',
      enabled: true,
      scope_type: 'operation',
      scope_id: 'op_btc_short_strangle_sep',
      operation_id: 'op_btc_short_strangle_sep',
      strategy_label: 'SHORT_STRANGLE',
      underlying: 'BTC',
      alert_type: 'leg_delta',
      channels: { in_app: true, email: false },
      condition: { threshold_value: 0.3, leg_scope: 'any_short' },
      cooldown_seconds: 21600,
      metadata: {},
      state: 'active',
      active_trigger_count: 1,
      condition_summary: 'any_short Δ >= 0,30',
      scope_label: 'BTC · SHORT_STRANGLE',
      availability: 'implemented',
      availability_reason: null,
      last_evaluated_at: daysAgo(0),
      last_triggered_at: daysAgo(0),
      operation_title: 'BTC · SHORT_STRANGLE',
      operation_expiration_label: '27 SEP 2026',
    },
    {
      id: 'demo-alert-3',
      enabled: false,
      scope_type: 'strategy_default',
      scope_id: 'SHORT_STRANGLE',
      operation_id: null,
      strategy_label: 'SHORT_STRANGLE',
      underlying: 'BTC',
      alert_type: 'dte_roll_window',
      channels: { in_app: true, email: false },
      condition: { threshold_value: 14 },
      cooldown_seconds: 86400,
      metadata: {},
      state: 'paused',
      active_trigger_count: 0,
      condition_summary: 'DTE <= 14',
      scope_label: 'SHORT_STRANGLE · default',
      availability: 'implemented',
      availability_reason: null,
      last_evaluated_at: daysAgo(1),
      last_triggered_at: null,
      operation_title: null,
      operation_expiration_label: null,
    },
  ],
  history: [
    {
      id: 'demo-event-1',
      rule_id: 'demo-alert-2',
      alert_type: 'leg_delta',
      severity: 'warning',
      title: 'Delta da perna chegou a 0,31',
      message: 'BTC · SHORT_STRANGLE · any_short Δ atual 0,31 · alerta >= 0,30',
      observed_value: 0.31,
      observed_value_label: 'Δ 0,31',
      threshold_label: 'Δ >= 0,30',
      operation_id: 'op_btc_short_strangle_sep',
      underlying: 'BTC',
      cta_path: '/options/positions',
      channels: { in_app: true, email: false },
      entity_key: 'op_btc_short_strangle_sep',
      triggered_at: daysAgo(0),
      created_at: daysAgo(0),
      read_at: null,
      delivery: { in_app: 'created', email: 'disabled' },
    },
    {
      id: 'demo-event-2',
      rule_id: 'demo-alert-1',
      alert_type: 'dte_roll_window',
      severity: 'warning',
      title: 'BTC · SHORT_STRANGLE entrou em 21 DTE',
      message: 'DTE atual 21,0 · seu alerta DTE <= 21',
      observed_value: 21,
      observed_value_label: '21 DTE',
      threshold_label: 'DTE <= 21',
      operation_id: 'op_btc_short_strangle_sep',
      underlying: 'BTC',
      cta_path: '/options/positions',
      channels: { in_app: true, email: true },
      entity_key: 'op_btc_short_strangle_sep',
      triggered_at: daysAgo(1),
      created_at: daysAgo(1),
      read_at: daysAgo(0),
      delivery: { in_app: 'created', email: 'skipped' },
    },
  ],
});

const readDemoAlertsState = () => {
  if (typeof window === 'undefined') return buildDefaultDemoAlertsState();
  try {
    const raw = window.localStorage.getItem(DEMO_ALERTS_STORAGE_KEY);
    if (!raw) return buildDefaultDemoAlertsState();
    return JSON.parse(raw);
  } catch {
    return buildDefaultDemoAlertsState();
  }
};

const writeDemoAlertsState = (state) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(DEMO_ALERTS_STORAGE_KEY, JSON.stringify(state));
};

const buildDemoAlertsDashboard = () => {
  const state = readDemoAlertsState();
  const activeRules = state.rules.filter((item) => item.enabled);
  const pausedRules = state.rules.filter((item) => !item.enabled);
  const unread = state.history.filter((item) => !item.read_at);
  return {
    summary: {
      active_rules: activeRules.length,
      paused_rules: pausedRules.length,
      recent_events: state.history.length,
      unread_notifications: unread.length,
      last_sync_at: daysAgo(0),
      evaluation_frequency: 'demo_local_only',
    },
    preferences: state.preferences,
    definitions: alertDefinitions,
    targets: buildDemoAlertTargets(),
    active_rules: activeRules,
    paused_rules: pausedRules,
    history: state.history,
    notifications: state.history.slice(0, 8),
  };
};

const persistDemoRuleMutation = (updater) => {
  const state = readDemoAlertsState();
  const nextState = updater(typeof structuredClone !== 'undefined' ? structuredClone(state) : JSON.parse(JSON.stringify(state)));
  writeDemoAlertsState(nextState);
  return buildDemoAlertsDashboard();
};

const buildDemoConditionSummary = (payload) => {
  const condition = payload.condition || {};
  if (payload.alert_type === 'dte_roll_window') return `DTE <= ${condition.threshold_value}`;
  if (payload.alert_type === 'leg_delta') return `${condition.leg_scope || 'any_short'} Δ >= ${condition.threshold_value}`;
  if (payload.alert_type === 'position_exposure') {
    return condition.mode === 'bias'
      ? `Bias = ${condition.target_bias}`
      : `Exposure ${condition.comparator} ${(Number(condition.threshold_value || 0) * 100).toFixed(1)}%`;
  }
  if (payload.alert_type === 'profit_target' || payload.alert_type === 'max_loss') {
    return condition.unit === 'usd'
      ? `P&L ${condition.comparator} $${condition.threshold_value}`
      : `P&L ${condition.comparator} ${condition.threshold_value} ${payload.underlying || ''}`.trim();
  }
  if (payload.alert_type === 'strike_proximity') return `<= ${(Number(condition.threshold_value || 0) * 100).toFixed(1)}% do strike`;
  if (payload.alert_type === 'margin_risk') return `${condition.metric} ${condition.comparator} ${condition.threshold_value}`;
  return payload.alert_type;
};

const createDemoRule = (payload) => persistDemoRuleMutation((state) => {
  const id = `demo-alert-${Date.now()}`;
  const operation = state.rules.find((item) => item.operation_id === payload.operation_id)?.operation_title
    || buildDemoAlertTargets().operations.find((item) => item.id === payload.operation_id)?.title
    || `${payload.underlying || 'BTC'} · ${payload.strategy_label || 'Alert'}`;
  state.rules.unshift({
    id,
    enabled: payload.enabled !== false,
    scope_type: payload.scope_type,
    scope_id: payload.scope_id || payload.operation_id || payload.strategy_label || payload.underlying,
    operation_id: payload.operation_id || null,
    strategy_label: payload.strategy_label || null,
    underlying: payload.underlying || null,
    alert_type: payload.alert_type,
    channels: payload.channels || { in_app: true, email: false },
    condition: payload.condition || {},
    cooldown_seconds: payload.cooldown_seconds || 21600,
    metadata: payload.metadata || {},
    state: payload.enabled === false ? 'paused' : 'active',
    active_trigger_count: 0,
    condition_summary: buildDemoConditionSummary(payload),
    scope_label: operation,
    availability: 'implemented',
    availability_reason: null,
    last_evaluated_at: daysAgo(0),
    last_triggered_at: null,
    operation_title: operation,
    operation_expiration_label: null,
  });
  return state;
});

const updateDemoRule = (ruleId, payload) => persistDemoRuleMutation((state) => {
  state.rules = state.rules.map((item) => item.id === ruleId ? { ...item, ...payload, channels: payload.channels || item.channels, condition: payload.condition || item.condition, condition_summary: buildDemoConditionSummary({ ...item, ...payload, condition: payload.condition || item.condition }) } : item);
  return state;
});

const toggleDemoRule = (ruleId, enabled) => persistDemoRuleMutation((state) => {
  state.rules = state.rules.map((item) => item.id === ruleId ? { ...item, enabled, state: enabled ? 'active' : 'paused' } : item);
  return state;
});

const deleteDemoRule = (ruleId) => persistDemoRuleMutation((state) => {
  state.rules = state.rules.filter((item) => item.id !== ruleId);
  return state;
});

const markDemoAlertRead = (eventId) => persistDemoRuleMutation((state) => {
  state.history = state.history.map((item) => item.id === eventId ? { ...item, read_at: daysAgo(0) } : item);
  return state;
});

const updateDemoAlertPreferences = (payload) => persistDemoRuleMutation((state) => {
  state.preferences = { ...state.preferences, ...payload, provider_ready: false };
  return state;
});

const buildCampaignsPayload = (limit = 100) => {
  const items = [...campaignSummaries].sort((a, b) => new Date(b.opened_at).getTime() - new Date(a.opened_at).getTime());
  return {
    items: items.slice(0, limit),
    total: items.length,
    limit,
  };
};

const buildEquityHistoryPayload = ({ range = '30d', currency = null, start = null, end = null, limit = 500 } = {}) => {
  const bounds = rangeBounds({ range, start, end });
  const filtered = equityHistory
    .filter((item) => !currency || item.currency === currency.toUpperCase())
    .filter((item) => (!bounds.startAt || item.snapshot_timestamp_ms >= bounds.startAt.getTime()) && item.snapshot_timestamp_ms <= bounds.endAt.getTime())
    .sort((a, b) => a.snapshot_timestamp_ms - b.snapshot_timestamp_ms);

  return {
    range: buildRangeMeta({ range, start, end }),
    currency_filter: currency ? currency.toUpperCase() : null,
    items: filtered.slice(-limit),
    total: filtered.length,
    limit,
  };
};

const buildAccountCurrentCapitalPeriod = () => {
  const btcCurrent = buildCurrentCapitalPeriodView(btcPeriods);
  const ethCurrent = buildCurrentCapitalPeriodView(ethPeriods);
  const startingUsd = Number(btcCurrent?.starting_equity_usd || 0) + Number(ethCurrent?.starting_equity_usd || 0);
  const currentUsd = Number(btcCurrent?.current_equity_usd || 0) + Number(ethCurrent?.current_equity_usd || 0);
  const latestFlow = [...cashFlowEvents].sort((a, b) => b.timestamp_ms - a.timestamp_ms)[0] || null;

  return {
    started_at: latestFlow?.timestamp || btcCurrent?.started_at || null,
    starting_equity_usd: startingUsd || null,
    current_equity_usd: currentUsd || null,
    return_pct: startingUsd > 0 ? Number(((currentUsd - startingUsd) / startingUsd).toFixed(12)) : null,
    return_pct_reason: startingUsd > 0 ? null : 'Equity inicial não positiva; retorno percentual do intervalo não é matematicamente confiável.',
    pnl_usd: startingUsd > 0 ? Number((currentUsd - startingUsd).toFixed(8)) : null,
    latest_change_type: latestFlow?.type || null,
    latest_change_currency: latestFlow?.currency || null,
    latest_change_amount_native: latestFlow?.amount_native ?? null,
    latest_change_amount_usd: latestFlow?.amount_usd ?? null,
    methodology: 'consolidated_account_return',
  };
};

const buildCurrentCapitalPeriodsPayload = ({ currency = null } = {}) => {
  const selectedCurrencies = currency ? [currency.toUpperCase()] : ['BTC', 'ETH'];
  return {
    generated_at: new Date().toISOString(),
    account: buildAccountCurrentCapitalPeriod(),
    currencies: selectedCurrencies.map((code) => ({
      currency: code,
      current_period: buildCurrentCapitalPeriodView(code === 'BTC' ? btcPeriods : ethPeriods),
      cash_flow_markers: cashFlowEvents.filter((item) => item.currency === code),
      absolute_trading_metrics: buildTradingSummaryForCurrency(code, rangeBounds({ range: 'all' })),
      equity_projection: buildDemoEquityProjection(code),
    })),
  };
};

const buildDemoReadOnlyError = () => {
  const error = new Error('DEMO PORTFOLIO is read-only');
  error.response = { data: { detail: 'DEMO PORTFOLIO is read-only.' } };
  return error;
};

export const getOptionsDemoOverview = async () => buildOverviewPayload();
export const getOptionsDemoAccount = async () => buildAccountPayload();
export const getOptionsDemoEquityHistory = async (payload) => buildEquityHistoryPayload(payload);
export const getOptionsDemoPerformance = async (payload) => buildPerformancePayload(payload);
export const getOptionsDemoAnalytics = async (payload) => buildAnalyticsPayload(payload || {});
export const getOptionsDemoCapitalPeriods = async (payload) => buildCapitalPeriodsPayload(payload || {});
export const getOptionsDemoCurrentCapitalPeriods = async (payload) => buildCurrentCapitalPeriodsPayload(payload || {});
export const getOptionsDemoExecutions = async (limit = 50) => ({ items: [], total: 0, limit });
export const getOptionsDemoLegs = async (limit = 100) => ({ items: [], total: 0, limit });
export const getOptionsDemoOperations = async (limit = 100) => buildOperationsPayload(limit);
export const getOptionsDemoOperationDetail = async (operationId) => operationDetailsMap[operationId] || { operation: null, legs: [] };
export const getOptionsDemoCampaigns = async (limit = 100) => buildCampaignsPayload(limit);
export const getOptionsDemoCampaignDetail = async (campaignId) => campaignDetailsMap[campaignId] || { campaign: null, operations: [], legs: [], roll_links: [] };
export const getOptionsDemoRollReviewQueue = async (limit = 100) => ({ items: [], total: 0, limit });
export const getOptionsDemoAlerts = async () => buildDemoAlertsDashboard();
export const createOptionsDemoAlertRule = async (payload) => createDemoRule(payload || {});
export const updateOptionsDemoAlertRule = async (ruleId, payload) => updateDemoRule(ruleId, payload || {});
export const toggleOptionsDemoAlertRule = async (ruleId, enabled) => toggleDemoRule(ruleId, enabled);
export const deleteOptionsDemoAlertRule = async (ruleId) => deleteDemoRule(ruleId);
export const markOptionsDemoAlertRead = async (eventId) => markDemoAlertRead(eventId);
export const updateOptionsDemoAlertPreferences = async (payload) => updateDemoAlertPreferences(payload || {});

export const rejectOptionsDemoMutation = async () => {
  throw buildDemoReadOnlyError();
};